import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from "../../StoreContext/LanguageContext";

import {
    Users, Edit, Plus, AlertCircle, CheckCircle, Eye, EyeOff, Building2,
    CreditCard, MapPin, FileText, X, Save, Loader2, Upload, Navigation,
    Play, Pause, Video, TreePine, Home, Factory, Waves, Droplets, Zap,
    Package, Fence, Camera, Menu, ArrowRight, Trash2, ChevronDown, ChevronUp
} from 'lucide-react';
import apiService from '../../config';

// ===================================================================
// UTILITY FUNCTIONS
// ===================================================================

const validateCultivableSplit = (irrigableArea, nonIrrigableArea, totalArea) => {
    const irrigable = parseFloat(irrigableArea) || 0;
    const nonIrrigable = parseFloat(nonIrrigableArea) || 0;
    const total = parseFloat(totalArea) || 0;

    if (total === 0) return '';

    const sum = irrigable + nonIrrigable;
    const tolerance = 0.01;

    if (Math.abs(sum - total) > tolerance) {
        return 'Invalid';
    }

    return '';
};

// ===================================================================
// SMALL INTERNAL COMPONENTS
// ===================================================================

const SectionCard = ({ children, className = "" }) => (
    <div className={`rounded-xl border border-gray-200 bg-white p-3 md:p-4 ${className}`}>
        {children}
    </div>
);

const SmallButton = ({ variant = "primary", size = "sm", children, className = "", ...props }) => {
    const baseClasses = "h-8 px-3 text-sm rounded-lg font-medium transition-colors flex items-center gap-2";
    const variants = {
        primary: "bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-400",
        secondary: "bg-gray-200 hover:bg-gray-300 text-gray-700 disabled:bg-gray-100",
        success: "bg-green-600 hover:bg-green-700 text-white disabled:bg-gray-400",
        danger: "bg-red-600 hover:bg-red-700 text-white disabled:bg-gray-400",
        warning: "bg-yellow-600 hover:bg-yellow-700 text-white disabled:bg-gray-400"
    };

    return (
        <button className={`${baseClasses} ${variants[variant]} ${className}`} {...props}>
            {children}
        </button>
    );
};

const Row = ({ label, value, className = "" }) => (
    <div className={`py-2 flex justify-between items-center border-b border-gray-100 last:border-0 ${className}`}>
        <span className="text-xs text-gray-500">{label}</span>
        <span className="text-sm font-medium">{value}</span>
    </div>
);

const Field = ({ label, children, error }) => (
    <div className="space-y-1">
        {label && <label className="block text-xs font-medium text-gray-700">{label}</label>}
        {children}
        {error && (
            <div className="flex items-center text-red-500 text-xs">
                <AlertCircle className="h-3 w-3 mr-1" />
                {error}
            </div>
        )}
    </div>
);

const HighwayAddOwner = ({
    onBack,
    newOwner: ownerData,
    setNewOwner: setOwnerData,
    onAdd,
    onCancel,
    onRefresh,
    landAcquisition,
    isOpen = true,
    onClose,
    prefillData
}) => {
    // ===================================================================
    // STATE MANAGEMENT
    // ===================================================================

    // Loading and processing states
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [validationErrors, setValidationErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // UI and navigation states
    const [currentStep, setCurrentStep] = useState(1);
    const [completedSections, setCompletedSections] = useState({});
    const [showAadhaar, setShowAadhaar] = useState(false);
    const [showAccount, setShowAccount] = useState(false);
    const [selectedMeasurement, setSelectedMeasurement] = useState(null);
    const [selectedLandAcquisitionId, setSelectedLandAcquisitionId] = useState(null);

    // Mobile-specific states
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    // Camera and media management states
    const [isCameraOpen, setIsCameraOpen] = useState(false);
    const [currentAssetType, setCurrentAssetType] = useState(null);
    const [currentAssetIndex, setCurrentAssetIndex] = useState(null);
    const [currentAssetData, setCurrentAssetData] = useState(null);
    const [capturedPhotos, setCapturedPhotos] = useState({});
    const [existingPhotos, setExistingPhotos] = useState({});
    const videoRef = useRef(null);
    const canvasRef = useRef(null);

    // Media states
    const [selectedImage, setSelectedImage] = useState(null);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [previewFile, setPreviewFile] = useState(null);
    const [isGettingLocation, setIsGettingLocation] = useState(false);
    const [videoPreview, setVideoPreview] = useState(null);
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);

    // Tree management states
    const [treeTypes, setTreeTypes] = useState([]);
    const [treeCategories, setTreeCategories] = useState([]);
    const [editingTreeId, setEditingTreeId] = useState(null);

    // Area states
    const [owner_irrigable_area, setOwnerIrrigableArea] = useState("");
    const [owner_non_irrigable_area, setOwnerNonIrrigableArea] = useState("");

    // Refs for scrolling containers
    const homesContainerRef = useRef(null);
    const tinShedContainerRef = useRef(null);
    const wireFenceContainerRef = useRef(null);
    const farmPondContainerRef = useRef(null);
    const treeContainerRef = useRef(null);
    const wellContainerRef = useRef(null);
    const borewellContainerRef = useRef(null);
    const pipelineContainerRef = useRef(null);
    const otherContainerRef = useRef(null);

    // Alert states
    const [showAlertModal, setShowAlertModal] = useState(false);
    const [alertConfig, setAlertConfig] = useState({
        title: '',
        message: '',
        type: 'success'
    });
    const { language, translations } = useLanguage();
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmConfig, setConfirmConfig] = useState({
        title: '',
        message: '',
        onConfirm: null,
        onCancel: null,
        type: 'warning'
    });

    // Other states
    const [owners, setOwners] = useState([]);
    const [uploadedDocuments, setUploadedDocuments] = useState([]);
    const [showAddOwner, setShowAddOwner] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    // Main form state
    const [newOwner, setNewOwner] = useState({
        // Basic fields
        owner_name: '',
        owner_type: '',
        owner_contact: '',
        owner_address: '',
        owner_email: '',
        nature_of_owner: '',
        aadhaar_number: '',
        pan_number: '',

        // Land area fields
        owner_total_area: '',
        owner_aqr_area: '',
        owner_irrigable_area: '',
        owner_non_irrigable_area: '',
        land_category: '',

        // Calculated fields
        total_percentage: '',
        solatium_percentage: '',

        // Location fields
        latitude: '',
        longitude: '',
        // Remarks fields - ADD THESE
        remarks: '',
        justification: '',

        owner_7_12_non_irrigable_area: '',
        owner_7_12_irrigable_area: '',
        // Asset arrays
        tree_entries: [],
        home_entries: [],
        tinshed_entries: [],
        wirefence_entries: [],
        farmpond_entries: [],
        well_entries: [],
        borewell_entries: [],
        pipeline_entries: [],
        other_entries: [],
    });

    // ===================================================================
    // CONFIGURATION
    // ===================================================================

    // Step definitions with complete metadata
    const STEP_DEFINITIONS = {
        1: { title: "Overview", icon: MapPin, color: "orange", description: "Property details and summary" },
        2: { title: "Trees", icon: TreePine, color: "green", description: "Tree inventory management" },
        3: { title: "Homes", icon: Home, color: "indigo", description: "Residential structures" },
        4: { title: "Tin Sheds", icon: Factory, color: "blue", description: "Industrial structures" },
        5: { title: "Wire Fencing", icon: Fence, color: "purple", description: "Boundary fencing" },
        6: { title: "Farm Ponds", icon: Waves, color: "blue", description: "Water reservoirs" },
        7: { title: "Wells", icon: Droplets, color: "cyan", description: "Water wells" },
        8: { title: "Borewells", icon: Zap, color: "blue", description: "Drilled wells" },
        9: { title: "Pipelines", icon: Zap, color: "sky", description: "Water pipes" },
        10: { title: "Others", icon: Package, color: "gray", description: "Miscellaneous assets" },
        11: { title: "Location", icon: Navigation, color: "indigo", description: "GPS and media" },
        12: { title: "Remarks", icon: FileText, color: "yellow", description: "Additional remarks and justification" },
    };

    // ===================================================================
    // UTILITY FUNCTIONS
    // ===================================================================

    /**
     * Shows alert notifications
     */

    //Validation for 7/12 is not more then JMR
    const validateAreas = () => {
        const errors = [];

        const irrigable_7_12 = parseFloat(newOwner.owner_7_12_irrigable_area || 0) || 0;
        const non_irrigable_7_12 = parseFloat(newOwner.owner_7_12_non_irrigable_area || 0) || 0;
        const irrigable_jmr = parseFloat(newOwner.owner_irrigable_area || 0) || 0;
        const non_irrigable_jmr = parseFloat(newOwner.owner_non_irrigable_area || 0) || 0;

        // Check if JMR cultivable area exceeds 7/12 cultivable area
        if (irrigable_jmr > irrigable_7_12) {
            errors.push(`JMR Cultivable area (${irrigable_jmr}) cannot exceed 7/12 Cultivable area (${irrigable_7_12})`);
        }

        // Check if JMR non-cultivable area exceeds 7/12 non-cultivable area
        if (non_irrigable_jmr > non_irrigable_7_12) {
            errors.push(`JMR Non-Cultivable area (${non_irrigable_jmr}) cannot exceed 7/12 Non-Cultivable area (${non_irrigable_7_12})`);
        }

        // Optional: Check if total JMR area exceeds total 7/12 area
        const total_jmr = irrigable_jmr + non_irrigable_jmr;
        const total_7_12 = irrigable_7_12 + non_irrigable_7_12;

        if (total_jmr > total_7_12) {
            errors.push(`Total JMR area (${total_jmr}) cannot exceed total 7/12 area (${total_7_12})`);
        }

        return errors;
    };


    const showAlert = useCallback((title, message, type = 'info') => {
        setAlertConfig({ title, message, type });
        setShowAlertModal(true);

        if (type !== 'success') {
            setTimeout(() => setShowAlertModal(false), 5000);
        }
    }, []);

    /**
     * Shows confirmation dialog
     */
    const showConfirm = useCallback((title, message, onConfirm, onCancel = null, type = 'warning') => {
        setConfirmConfig({ title, message, onConfirm, onCancel, type });
        setShowConfirmModal(true);
    }, []);

    /**
     * Safe number parsing
     */
    const safeParseFloat = (value) => {
        const parsed = parseFloat(value);
        return isNaN(parsed) || !isFinite(parsed) ? null : parsed;
    };

    const safeParseInt = (value) => {
        const parsed = parseInt(value);
        return isNaN(parsed) || !isFinite(parsed) ? null : parsed;
    };

    /**
     * Field validation
     */
    const validateField = useCallback((name, value) => {
        switch (name) {
            case 'owner_type':
                if (!value) {
                    return 'Occupant type is required';
                }
                break;

            case 'owner_contact':
                if (name === 'owner_contact') {
                    const cleaned = value.replace(/\D/g, '');
                    const isMobile = /^(\d{10})$/.test(cleaned);
                    const isLandline = /^0\d{10}$/.test(cleaned);

                    if (!isMobile && !isLandline) {
                        return 'Enter a valid 10-digit mobile or 11-digit landline number (with 0)';
                    }
                }
                break;

            case 'owner_address':
                if (!value || value.trim().length < 10) {
                    return 'Address must be at least 10 characters long';
                }
                break;

            case 'aadhaar_number':
                if (['Individual', 'Joint'].includes(newOwner.owner_type)) {
                    if (!value) {
                        return 'Aadhaar number is required';
                    }
                    if (!/^\d{12}$/.test(value)) {
                        return 'Aadhaar number must be exactly 12 digits';
                    }
                } else if (value && !/^\d{12}$/.test(value)) {
                    return 'Aadhaar number must be exactly 12 digits';
                }
                break;

            case 'pan_number':
                if (['Individual', 'Joint'].includes(newOwner.owner_type)) {
                    if (!value) {
                        return 'PAN number is required';
                    }
                    if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value)) {
                        return 'Invalid PAN number (e.g. ABCDE1234F)';
                    }
                } else if (value && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value)) {
                    return 'Invalid PAN number (e.g. ABCDE1234F)';
                }
                break;

            default:
                break;
        }
        return '';
    }, [newOwner.owner_type, landAcquisition]);

    /**
     * Handle field changes
     */
    const handleFieldChange = useCallback((name, value) => {
        console.log(`Updating field: ${name} =`, value);

        setNewOwner(prev => ({ ...prev, [name]: value }));

        const error = validateField(name, value);
        setValidationErrors(prev => ({
            ...prev,
            [name]: error
        }));
    }, [validateField]);

    // ===================================================================
    // TREE MANAGEMENT FUNCTIONS
    // ===================================================================

    /**
     * Gets unique tree categories
     */
    const getUniqueCategories = useCallback(() => {
        return [...new Set(treeTypes.map(type => type.tree_type_name1))].filter(Boolean);
    }, [treeTypes]);

    /**
     * Gets trees by category
     */
    const getTreesByCategory = useCallback((category) => {
        if (!category) return [];
        return treeTypes.filter(type => type.tree_type_name1 === category);
    }, [treeTypes]);

    /**
     * Adds new tree entry
     */
    const addTreeEntry = useCallback(() => {
        const currentEntries = newOwner.tree_entries || [];
        const newEntry = {
            count: "",
            category: "",
            type_id: "",
            other_type: "",
            tree_value_valuer: "",
            tree_value_lao: "",
            saved: false,
            document: null
        };
        const newEntries = [...currentEntries, newEntry];
        handleFieldChange("tree_entries", newEntries);
    }, [newOwner.tree_entries, handleFieldChange]);

    /**
     * Updates tree entry
     */
    const updateTreeEntry = useCallback((index, field, value) => {
        const updatedEntries = [...(newOwner.tree_entries || [])];

        if (field === 'category') {
            updatedEntries[index] = {
                ...updatedEntries[index],
                category: value,
                type_id: "",
                other_type: "",
                saved: false
            };
        } else if (field === 'type_id') {
            updatedEntries[index] = {
                ...updatedEntries[index],
                type_id: value,
                other_type: value === "other" ? updatedEntries[index].other_type : "",
                saved: false
            };
        } else {
            updatedEntries[index] = {
                ...updatedEntries[index],
                [field]: value,
                saved: false
            };
        }

        handleFieldChange("tree_entries", updatedEntries);
        updateTreeFieldFormat(updatedEntries);
    }, [newOwner.tree_entries, handleFieldChange]);

    /**
     * Saves tree entry to API with validation (like EditOwner)
     */
    const saveTree = useCallback(async (entry, index, ownerId) => {
        // Enhanced validation like EditOwner
        if (!entry.count || entry.count.trim() === "" || parseInt(entry.count) <= 0) {
            showAlert("Validation Error", "Please enter a valid tree count", "error");
            return false;
        }

        if (!entry.category || entry.category.trim() === "") {
            showAlert("Validation Error", "Please select a tree category", "error");
            return false;
        }

        if (!entry.type_id || entry.type_id.trim() === "") {
            showAlert("Validation Error", "Please select a tree type", "error");
            return false;
        }

        if (entry.type_id === "other" && (!entry.other_type || entry.other_type.trim() === "")) {
            showAlert("Validation Error", "Please enter custom tree type name", "error");
            return false;
        }

        try {
            setSaving(true);

            let treeTypeName = "";
            if (entry.type_id === "other") {
                treeTypeName = entry.other_type.trim();
            } else {
                const treeType = treeTypes.find(
                    (type) => type.tree_type_id === parseInt(entry.type_id)
                );
                treeTypeName = treeType ? treeType.tree_type_name : "";
            }

            // Create FormData for multipart/form-data
            const formData = new FormData();
            formData.append('land_owner_id', ownerId);
            formData.append('tree_type_name', treeTypeName);
            formData.append('tree_count', parseInt(entry.count));
            formData.append('tree_value_valuer', entry.tree_value_valuer || "");
            formData.append('tree_value_lao', entry.tree_value_lao || "");

            // Add captured photo if available
            const photoKey = `trees_${index}`;
            const capturedPhoto = capturedPhotos[photoKey];

            console.log(`Saving tree - Looking for photo key: ${photoKey}, Found: ${!!capturedPhoto?.blob}`);

            if (capturedPhoto && capturedPhoto.blob) {
                // Convert blob to file with proper filename
                const file = new File([capturedPhoto.blob], `tree_${ownerId}_${index}_${Date.now()}.jpg`, {
                    type: 'image/jpeg'
                });
                formData.append('document', file);
            }

            console.log("Saving tree with FormData:", {
                land_owner_id: ownerId,
                tree_type_name: treeTypeName,
                tree_count: parseInt(entry.count),
                hasPhoto: !!capturedPhoto?.blob,
                photoKey: photoKey
            });

            const result = await apiService.post("/postAddHighwayTree", formData);

            if (result.status === 200) {
                const updatedEntries = [...(newOwner.tree_entries || [])];
                updatedEntries[index] = {
                    ...updatedEntries[index],
                    tree_id: result.data?.tree_id || result.treeId || `temp_${Date.now()}`,
                    land_owner_id: ownerId,
                    saved: true,
                };
                handleFieldChange("tree_entries", updatedEntries);
                updateTreeFieldFormat(updatedEntries);

                setEditingTreeId(null);
                setError("");
                showAlert("Success", "Tree saved successfully with photo!", "success");
                return true;
            } else {
                showAlert("Error", result.message || "Failed to save tree", "error");
                return false;
            }
        } catch (err) {
            console.error("Error saving tree:", err);
            showAlert("Error", "Error saving tree: " + err.message, "error");
            return false;
        } finally {
            setSaving(false);
        }
    }, [newOwner.tree_entries, treeTypes, handleFieldChange, showAlert, capturedPhotos]);

    /**
     * Updates existing tree
     */
    const updateTree = useCallback(async (treeId, entry, index) => {
        try {
            setSaving(true);

            let treeTypeName = "";
            if (entry.type_id === "other") {
                treeTypeName = entry.other_type.trim();
            } else {
                const treeType = treeTypes.find(
                    (type) => type.tree_type_id === parseInt(entry.type_id)
                );
                treeTypeName = treeType ? treeType.tree_type_name : "";
            }

            // Create FormData for multipart/form-data
            const formData = new FormData();
            formData.append('tree_type_name', treeTypeName);
            formData.append('tree_count', parseInt(entry.count));

            // Add captured photo if available
            const photoKey = `trees_${index}`;
            const capturedPhoto = capturedPhotos[photoKey];

            if (capturedPhoto && capturedPhoto.blob) {
                // Convert blob to file with proper filename
                const file = new File([capturedPhoto.blob], `tree_update_${treeId}_${Date.now()}.jpg`, {
                    type: 'image/jpeg'
                });
                formData.append('document', file);
            }

            // Add manually uploaded document if present (fallback)
            if (entry.document && !capturedPhoto?.blob) {
                formData.append('document', entry.document);
            }

            const result = await apiService.patch(`/patchUpdateHighwayTree?treeId=${treeId}`, formData);

            if (result.status === 200) {
                const updatedEntries = [...(newOwner.tree_entries || [])];
                updatedEntries[index] = { ...updatedEntries[index], saved: true };
                handleFieldChange("tree_entries", updatedEntries);

                showAlert("Success", "Tree updated successfully with photo!", "success");
            } else {
                showAlert("Error", result.message || "Failed to update tree", "error");
            }
        } catch (err) {
            console.error("Error updating tree:", err);
            showAlert("Error", "Error updating tree: " + err.message, "error");
        } finally {
            setSaving(false);
        }
    }, [newOwner.tree_entries, treeTypes, handleFieldChange, showAlert, capturedPhotos]);

    /**
     * Removes tree entry
     */
    const removeTreeEntry = useCallback((index) => {
        const updatedEntries = (newOwner.tree_entries || []).filter((_, i) => i !== index);
        handleFieldChange("tree_entries", updatedEntries);
        updateTreeFieldFormat(updatedEntries);
    }, [newOwner.tree_entries, handleFieldChange]);

    // ===================================================================
    // PROPERTY API INTEGRATION
    // ===================================================================

    /**
     * Generic function to save property
     */
    const saveProperty = useCallback(async (entry, index, ownerId, propertyType, validationFn, payloadFn) => {
        try {
            // Run validation
            const validationResult = validationFn(entry);
            if (!validationResult.isValid) {
                showAlert("Validation Error", validationResult.message, "error");
                return;
            }

            setSaving(true);

            const formData = new FormData();
            formData.append('land_owner_id', ownerId);
            formData.append('property_type', propertyType);

            // Add property-specific fields
            const payload = payloadFn(entry);
            Object.entries(payload).forEach(([key, value]) => {
                if (value !== null && value !== undefined) {
                    formData.append(key, value);
                }
            });

            // Add captured photo if available - FIXED PHOTO KEY MAPPING
            const assetKey = propertyType.toLowerCase().replace('/', '_').replace('godown', 'shed');
            let photoKey;

            // Map property types to photo keys correctly
            switch (propertyType) {
                case 'tinshed/Godown':
                    photoKey = `tinsheds_${index}`;
                    break;
                case 'home':
                    photoKey = `homes_${index}`;
                    break;
                case 'wireFencing':
                    photoKey = `wirefences_${index}`;
                    break;
                case 'farmPond':
                    photoKey = `farmponds_${index}`;
                    break;
                case 'well':
                    photoKey = `wells_${index}`;
                    break;
                case 'borewell':
                    photoKey = `borewells_${index}`;
                    break;
                case 'pipeline':
                    photoKey = `pipelines_${index}`;
                    break;
                case 'other':
                    photoKey = `others_${index}`;
                    break;
                default:
                    photoKey = `${assetKey}_${index}`;
            }

            const capturedPhoto = capturedPhotos[photoKey];

            console.log(`Saving ${propertyType} - Looking for photo key: ${photoKey}, Found: ${!!capturedPhoto?.blob}`);

            if (capturedPhoto && capturedPhoto.blob) {
                // Convert blob to file with proper filename
                const file = new File([capturedPhoto.blob], `${propertyType}_${ownerId}_${index}_${Date.now()}.jpg`, {
                    type: 'image/jpeg'
                });
                formData.append('document', file);
            }

            // Add manually uploaded document if present
            if (entry.document) {
                formData.append('document', entry.document);
            }

            console.log("Saving property with FormData:", {
                property_type: propertyType,
                land_owner_id: ownerId,
                hasPhoto: !!capturedPhoto?.blob,
                hasDocument: !!entry.document,
                photoKey: photoKey
            });

            const result = await apiService.post("/postAddHighwayProperty", formData);

            if (result.status === 200) {
                // Update the entry as saved
                const assetKey = `${propertyType.toLowerCase().replace('/', '_')}_entries`;
                const updatedEntries = [...(newOwner[assetKey] || [])];
                updatedEntries[index] = {
                    ...updatedEntries[index],
                    property_id: result.data?.property_id || result.propertyId || `temp_${Date.now()}`,
                    land_owner_id: ownerId,
                    saved: true,
                };
                handleFieldChange(assetKey, updatedEntries);

                showAlert("Success", `${propertyType} saved successfully with photo!`, "success");
            } else {
                showAlert("Error", result.message || `Failed to save ${propertyType}`, "error");
            }
        } catch (err) {
            console.error(`Error saving ${propertyType}:`, err);
            showAlert("Error", `Error saving ${propertyType}: ` + err.message, "error");
        } finally {
            setSaving(false);
        }
    }, [newOwner, handleFieldChange, showAlert, capturedPhotos]);

    // ===================================================================
    // HOME MANAGEMENT FUNCTIONS
    // ===================================================================

    const addHomeEntry = useCallback(() => {
        const currentHomes = newOwner.home_entries || [];
        const newHome = {
            floors: [{
                type: "ground_floor",
                length: "",
                width: "",
                construction_type: "Permanent House",
                custom_construction_type: "",
            }],
            saved: false,
            document: null
        };
        const newHomes = [...currentHomes, newHome];
        handleFieldChange("home_entries", newHomes);
    }, [newOwner.home_entries, handleFieldChange]);

    const updateHomeEntry = useCallback((homeIndex, floorIndex, field, value) => {
        const updatedHomes = [...(newOwner.home_entries || [])];
        updatedHomes[homeIndex].floors[floorIndex] = {
            ...updatedHomes[homeIndex].floors[floorIndex],
            [field]: value,
        };
        updatedHomes[homeIndex].saved = false;
        handleFieldChange("home_entries", updatedHomes);
    }, [newOwner.home_entries, handleFieldChange]);

    const removeHomeEntry = useCallback((index) => {
        const updatedHomes = (newOwner.home_entries || []).filter((_, i) => i !== index);
        handleFieldChange("home_entries", updatedHomes);
        updateHomeFieldsAndFormat(updatedHomes);
    }, [newOwner.home_entries, handleFieldChange]);

    // ===================================================================
    // ASSET HANDLERS WITH API INTEGRATION
    // ===================================================================

    /**
     * Home property handlers
     */
    const homeHandlers = {
        add: addHomeEntry,
        update: updateHomeEntry,
        remove: removeHomeEntry,
        save: async (entry, index, ownerId) => {
            const validationFn = (entry) => {
                const floor = entry.floors?.[0] || {};
                if (!floor.length || !floor.width) {
                    return { isValid: false, message: "Please enter length and width" };
                }
                if (!floor.construction_type) {
                    return { isValid: false, message: "Please select construction type" };
                }
                if (floor.construction_type === "Other" && !floor.custom_construction_type) {
                    return { isValid: false, message: "Please enter custom construction type" };
                }
                return { isValid: true };
            };

            const payloadFn = (entry) => {
                const floor = entry.floors[0];
                return {
                    house_floor_no: floor.type || 'ground',
                    Property_width: parseFloat(floor.width),
                    Property_length: parseFloat(floor.length),
                    total: parseFloat(floor.width) * parseFloat(floor.length),
                    construction_type: floor.construction_type === "Other" ? floor.custom_construction_type : floor.construction_type
                };
            };

            await saveProperty(entry, index, ownerId, 'home', validationFn, payloadFn);
        }
    };

    /**
     * Tin Shed property handlers
     */
    const tinShedHandlers = {
        add: () => {
            const entry = {
                count: "",
                length: "",
                width: "",
                saved: false,
                document: null
            };
            const updated = [...(newOwner.tinshed_entries || []), entry];
            handleFieldChange('tinshed_entries', updated);
        },

        update: (index, field, value) => {
            const updated = [...(newOwner.tinshed_entries || [])];
            updated[index][field] = value;
            updated[index].saved = false;
            handleFieldChange('tinshed_entries', updated);
        },

        remove: (index) => {
            const updated = (newOwner.tinshed_entries || []).filter((_, i) => i !== index);
            handleFieldChange('tinshed_entries', updated);
        },
        
        save: async (entry, index, ownerId) => {
            const validationFn = (entry) => {
                if (!entry.length || !entry.width) {
                    return { isValid: false, message: "Please enter length and width" };
                }
                return { isValid: true };
            };

            const payloadFn = (entry) => ({
                Property_width: parseFloat(entry.width),
                Property_length: parseFloat(entry.length),
                total: parseFloat(entry.width) * parseFloat(entry.length)
            });

            await saveProperty(entry, index, ownerId, 'tinshed/Godown', validationFn, payloadFn);
        }
    };

    /**
     * Wire Fence property handlers
     */
    const wireFenceHandlers = {
        add: () => {
            const entry = {
                pipes: [{ type: "pipe_1", length: "", height: "2" }],
                saved: false,
                document: null
            };
            const updated = [...(newOwner.wirefence_entries || []), entry];
            handleFieldChange('wirefence_entries', updated);
        },
        update: (index, field, value) => {
            const updated = [...(newOwner.wirefence_entries || [])];
            if (field.includes('.')) {
                const [parent, child] = field.split('.');
                const parentIndex = parseInt(parent.match(/\d+/)[0]);
                const parentKey = parent.replace(/\[\d+\]/, '');
                updated[index][parentKey][parentIndex][child] = value;
            } else {
                updated[index][field] = value;
            }
            updated[index].saved = false;
            handleFieldChange('wirefence_entries', updated);
        },
        remove: (index) => {
            const updated = (newOwner.wirefence_entries || []).filter((_, i) => i !== index);
            handleFieldChange('wirefence_entries', updated);
        },
        save: async (entry, index, ownerId) => {
            const validationFn = (entry) => {
                const pipe = entry.pipes?.[0] || {};
                if (!pipe.length) {
                    return { isValid: false, message: "Please enter fence length" };
                }
                return { isValid: true };
            };

            const payloadFn = (entry) => {
                const pipe = entry.pipes[0];
                return {
                    Property_length: parseFloat(pipe.length),
                    total: parseFloat(pipe.length) * (parseFloat(pipe.height) || 2)
                };
            };

            await saveProperty(entry, index, ownerId, 'wireFencing', validationFn, payloadFn);
        }
    };

    /**
     * Farm Pond property handlers
     */
    const farmPondHandlers = {
        add: () => {
            const entry = {
                dimensions: [{ length: "", width: "", depth: "" }],
                saved: false,
                document: null
            };
            const updated = [...(newOwner.farmpond_entries || []), entry];
            handleFieldChange('farmpond_entries', updated);
        },
        update: (index, field, value) => {
            const updated = [...(newOwner.farmpond_entries || [])];
            if (field.includes('.')) {
                const [parent, child] = field.split('.');
                const parentIndex = parseInt(parent.match(/\d+/)[0]);
                const parentKey = parent.replace(/\[\d+\]/, '');
                updated[index][parentKey][parentIndex][child] = value;
            } else {
                updated[index][field] = value;
            }
            updated[index].saved = false;
            handleFieldChange('farmpond_entries', updated);
        },
        remove: (index) => {
            const updated = (newOwner.farmpond_entries || []).filter((_, i) => i !== index);
            handleFieldChange('farmpond_entries', updated);
        },
        save: async (entry, index, ownerId) => {
            const validationFn = (entry) => {
                const dim = entry.dimensions?.[0] || {};
                if (!dim.length || !dim.width || !dim.depth) {
                    return { isValid: false, message: "Please enter length, width, and depth" };
                }
                return { isValid: true };
            };

            const payloadFn = (entry) => {
                const dim = entry.dimensions[0];
                return {
                    Property_width: parseFloat(dim.width),
                    Property_length: parseFloat(dim.length),
                    Property_depth: parseFloat(dim.depth),
                    total: parseFloat(dim.length) * parseFloat(dim.width) * parseFloat(dim.depth)
                };
            };

            await saveProperty(entry, index, ownerId, 'farmPond', validationFn, payloadFn);
        }
    };

    /**
     * Well property handlers
     */
    const wellHandlers = {
        add: () => {
            const entry = {
                diameter: "",
                depth: "",
                saved: false,
                document: null
            };
            const updated = [...(newOwner.well_entries || []), entry];
            handleFieldChange('well_entries', updated);
        },
        update: (index, field, value) => {
            const updated = [...(newOwner.well_entries || [])];
            updated[index][field] = value;
            updated[index].saved = false;
            handleFieldChange('well_entries', updated);
        },
        remove: (index) => {
            const updated = (newOwner.well_entries || []).filter((_, i) => i !== index);
            handleFieldChange('well_entries', updated);
        },
        save: async (entry, index, ownerId) => {
            const validationFn = (entry) => {
                if (!entry.diameter || !entry.depth) {
                    return { isValid: false, message: "Please enter diameter and depth" };
                }
                return { isValid: true };
            };

            const payloadFn = (entry) => ({
                Property_width: parseFloat(entry.diameter),
                Property_length: parseFloat(entry.diameter),
                Property_depth: parseFloat(entry.depth),
                total: Math.PI * Math.pow(parseFloat(entry.diameter) / 2, 2) * parseFloat(entry.depth)
            });

            await saveProperty(entry, index, ownerId, 'well', validationFn, payloadFn);
        }
    };

    /**
     * Borewell property handlers
     */
    const borewellHandlers = {
        add: () => {
            const entry = {
                depth: "",
                saved: false,
                document: null
            };
            const updated = [...(newOwner.borewell_entries || []), entry];
            handleFieldChange('borewell_entries', updated);
        },
        update: (index, field, value) => {
            const updated = [...(newOwner.borewell_entries || [])];
            updated[index][field] = value;
            updated[index].saved = false;
            handleFieldChange('borewell_entries', updated);
        },
        remove: (index) => {
            const updated = (newOwner.borewell_entries || []).filter((_, i) => i !== index);
            handleFieldChange('borewell_entries', updated);
        },
        save: async (entry, index, ownerId) => {
            const validationFn = (entry) => {
                if (!entry.depth) {
                    return { isValid: false, message: "Please enter count and depth" };
                }
                return { isValid: true };
            };

            const payloadFn = (entry) => ({

                Property_depth: parseFloat(entry.depth),
                total: parseFloat(entry.depth)
            });

            await saveProperty(entry, index, ownerId, 'borewell', validationFn, payloadFn);
        }
    };

    /**
     * Pipeline property handlers
     */
    const pipelineHandlers = {
        add: () => {
            const entry = {
                length: "",
                diameter : "",
                saved: false,
                document: null
            };
            const updated = [...(newOwner.pipeline_entries || []), entry];
            handleFieldChange('pipeline_entries', updated);
        },
        update: (index, field, value) => {
            const updated = [...(newOwner.pipeline_entries || [])];
            updated[index][field] = value;
            updated[index].saved = false;
            handleFieldChange('pipeline_entries', updated);
        },
        remove: (index) => {
            const updated = (newOwner.pipeline_entries || []).filter((_, i) => i !== index);
            handleFieldChange('pipeline_entries', updated);
        },
        save: async (entry, index, ownerId) => {
            const validationFn = (entry) => {
                if (!entry.length || !entry.Property_width ) {
                    return { isValid: false, message: "Please enter pipeline length and diameter" };
                }
                if (parseFloat(entry.length) <= 0 || parseFloat(entry.Property_width ) <= 0) {
                    return { isValid: false, message: "Length and diameter must be greater than 0" };
                }
                return { isValid: true };
            };

            const payloadFn = (entry) => {
                const length = parseFloat(entry.length) || 0;
                const width = parseFloat(entry.Property_width) || 0;
                return {
                    Property_length: length,
                    Property_width: width,
                    total: length * width,
                };
            };

            await saveProperty(entry, index, ownerId, 'pipeline', validationFn, payloadFn);
        }
    };

    /**
     * Other property handlers
     */
    const otherHandlers = {
        add: () => {
            const entry = {
                name: "",
                quantity: "",
                saved: false,
                document: null
            };
            const updated = [...(newOwner.other_entries || []), entry];
            handleFieldChange('other_entries', updated);
        },
        update: (index, field, value) => {
            const updated = [...(newOwner.other_entries || [])];
            updated[index][field] = value;
            updated[index].saved = false;
            handleFieldChange('other_entries', updated);
        },
        remove: (index) => {
            const updated = (newOwner.other_entries || []).filter((_, i) => i !== index);
            handleFieldChange('other_entries', updated);
        },
        save: async (entry, index, ownerId) => {
            const validationFn = (entry) => {
                if (!entry.name || !entry.quantity) {
                    return { isValid: false, message: "Please enter asset name and quantity" };
                }
                return { isValid: true };
            };

            const payloadFn = (entry) => ({
                construction_type: entry.name,
                Property_count: parseFloat(entry.quantity),
                total: parseFloat(entry.quantity)
            });

            await saveProperty(entry, index, ownerId, 'other', validationFn, payloadFn);
        }
    };

    // ===================================================================
    // FORMAT UPDATE FUNCTIONS
    // ===================================================================

    const updateTreeFieldFormat = useCallback((entries) => {
        if (!entries || entries.length === 0) {
            handleFieldChange('no_of_tree', '0');
            return;
        }

        const validEntries = entries.filter(entry =>
            entry.count && (entry.type_id || entry.other_type) && parseInt(entry.count) > 0
        );

        const totalTreeCount = validEntries.reduce((sum, entry) => {
            return sum + parseInt(entry.count);
        }, 0);

        handleFieldChange('no_of_tree', totalTreeCount.toString());
    }, [handleFieldChange]);

    const updateHomeFieldsAndFormat = useCallback((homeEntries) => {
        const homeCount = homeEntries?.length || 0;
        handleFieldChange('no_of_home', homeCount.toString());
    }, [handleFieldChange]);

    // ===================================================================
    // FETCH EXISTING PROPERTIES
    // ===================================================================

    /**
     * Fetch existing properties for an owner
     */
    const fetchOwnerProperties = useCallback(async (ownerId) => {
        try {
            setLoading(true);

            // Fetch trees
            const treesResponse = await apiService.get(`/getHighwayTree?ownerId=${ownerId}`);
            if (treesResponse.status === 200 && treesResponse.data) {
                const treeEntries = treesResponse.data.map(tree => ({
                    tree_id: tree.tree_id,
                    land_owner_id: tree.land_owner_id,
                    count: tree.tree_count.toString(),
                    category: tree.tree_category || "",
                    type_id: tree.tree_type_id ? tree.tree_type_id.toString() : "other",
                    other_type: tree.tree_type_name,
                    tree_value_valuer: tree.tree_value_valuer || "",
                    tree_value_lao: tree.tree_value_lao || "",
                    saved: true,
                    document: null
                }));
                handleFieldChange("tree_entries", treeEntries);
            }

            // Fetch properties by type
            const propertyTypes = ['home', 'tinshed/Godown', 'wireFencing', 'farmPond', 'well', 'borewell', 'pipeline', 'other'];

            for (const propertyType of propertyTypes) {
                const response = await apiService.get(`/getHighwayProperty?ownerId=${ownerId}&type=${propertyType}`);
                if (response.status === 200 && response.data) {
                    const assetKey = `${propertyType.toLowerCase().replace('/', '_')}_entries`;
                    const entries = response.data.map(property => ({
                        ...property,
                        saved: true,
                        document: null
                    }));
                    handleFieldChange(assetKey, entries);
                }
            }

        } catch (error) {
            console.error('Error fetching owner properties:', error);
            showAlert('Error', 'Failed to fetch existing properties', 'error');
        } finally {
            setLoading(false);
        }
    }, [handleFieldChange, showAlert]);

    // ===================================================================
    // MAIN OWNER CREATION FUNCTION
    // ===================================================================

    const createOwnerMeasurement = async () => {
        try {
            const formData = new FormData();

            const acquisitionId = landAcquisition?.LandAcquisition_id;
            if (!acquisitionId) {
                throw new Error('Land Acquisition data not found');
            }

            // Basic owner information for measurement
            formData.append('project_id', landAcquisition?.project_id || '');
            formData.append('proposal_id', landAcquisition?.proposal_id || '');
            formData.append('LandAcquisition_id', String(acquisitionId));
            formData.append('dist_name', landAcquisition?.dist_name || '');
            formData.append('taluka_name', landAcquisition?.taluka_name || '');
            formData.append('village_name', landAcquisition?.village_name || '');
            formData.append('survey_no', landAcquisition?.survey_no || '');

            // Owner details - numeric fields
            const numericFields = {
                'owner_total_area': safeParseFloat(newOwner.owner_total_area),
                'owner_aqr_area': safeParseFloat(newOwner.owner_aqr_area),
                'solatium_percentage': safeParseFloat(newOwner.solatium_percentage),
                'total_percentage': safeParseFloat(newOwner.total_percentage),
                'latitude': safeParseFloat(newOwner.latitude),
                'longitude': safeParseFloat(newOwner.longitude)
            };

            Object.entries(numericFields).forEach(([key, value]) => {
                if (value !== null && value !== undefined) {
                    formData.append(key, String(value));
                }
            });

            // String fields
            const stringFields = {
                'owner_name': newOwner.owner_name,
                'owner_type': newOwner.owner_type,
                'owner_contact': newOwner.owner_contact,
                'owner_address': newOwner.owner_address,
                'owner_email': newOwner.owner_email,
                'nature_of_owner': newOwner.nature_of_owner,
                'aadhaar_number': newOwner.aadhaar_number,
                'pan_number': newOwner.pan_number,
                'land_category': newOwner.land_category,
                'land_use': newOwner.land_use,
                'remarks': newOwner.remarks,
                'justification': newOwner.justification,
                'owner_7_12_non_irrigable_area': newOwner.owner_7_12_non_irrigable_area,
                'owner_7_12_irrigable_area': newOwner.owner_7_12_irrigable_area,
                'owner_irrigable_area': newOwner.owner_irrigable_area,
                'owner_non_irrigable_area': newOwner.owner_non_irrigable_area,
            };

            Object.entries(stringFields).forEach(([key, value]) => {
                if (value && value.trim()) {
                    formData.append(key, value.trim());
                }
            });

            // Add cultivable and non-cultivable area
            const irrigableArea = safeParseFloat(owner_irrigable_area);
            const nonIrrigableArea = safeParseFloat(owner_non_irrigable_area);

            if (irrigableArea !== null) {
                formData.append('owner_irrigable_area', String(irrigableArea));
            }
            if (nonIrrigableArea !== null) {
                formData.append('owner_non_irrigable_area', String(nonIrrigableArea));
            }

            // Add files if present
            if (selectedImage) {
                formData.append('image', selectedImage, selectedImage.name);
            }
            if (selectedVideo) {
                formData.append('video', selectedVideo, selectedVideo.name);
            }

            const response = await apiService.addHighwayMeasurement(formData);

            if (response && response.success !== false && response.ownerId) {
                return response.ownerId;
            } else {
                throw new Error(response?.message || 'Failed to create owner measurement');
            }
        } catch (error) {
            console.error('Error creating owner measurement:', error);
            throw error;
        }
    };

    // ===================================================================
    // GPS AND CAMERA FUNCTIONS
    // ===================================================================

    const getCurrentLocation = () => {
        setIsGettingLocation(true);

        if (!navigator.geolocation) {
            showAlert('Error', 'Geolocation is not supported by this browser.', 'error');
            setIsGettingLocation(false);
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const { latitude, longitude } = position.coords;
                handleFieldChange('latitude', latitude.toFixed(8));
                handleFieldChange('longitude', longitude.toFixed(8));
                showAlert('Success', `Location retrieved successfully! Accuracy: ${position.coords.accuracy.toFixed(0)}m`, 'success');
                setIsGettingLocation(false);
            },
            (error) => {
                let errorMessage = 'Unable to retrieve your location.';
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        errorMessage = 'Location access denied. Please enable location permissions.';
                        break;
                    case error.POSITION_UNAVAILABLE:
                        errorMessage = 'Location information is unavailable.';
                        break;
                    case error.TIMEOUT:
                        errorMessage = 'Location request timed out. Please try again.';
                        break;
                }
                showAlert('Error', errorMessage, 'error');
                setIsGettingLocation(false);
            },
            {
                enableHighAccuracy: true,
                timeout: 15000,
                maximumAge: 0
            }
        );
    };

    const openMapView = () => {
        if (newOwner.latitude && newOwner.longitude) {
            const mapUrl = `https://www.google.com/maps?q=${newOwner.latitude},${newOwner.longitude}&z=18`;
            window.open(mapUrl, '_blank');
        } else {
            showAlert('Warning', 'Please set coordinates first before viewing on map.', 'warning');
        }
    };

    const handleVideoSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 100 * 1024 * 1024) {
                showAlert('Error', 'Video file size should be less than 100MB', 'error');
                return;
            }
            setSelectedVideo(file);
            setVideoPreview(URL.createObjectURL(file));
            handleFieldChange('video', file);
        }
    };

    const handleImageSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 10 * 1024 * 1024) {
                showAlert('Error', 'Image file size should be less than 10MB', 'error');
                return;
            }
            setSelectedImage(file);
            setImagePreview(URL.createObjectURL(file));
            handleFieldChange('image', file);
        }
    };

    // Enhanced Camera functionality
    const openCamera = async (assetType, assetIndex, assetData = null) => {
        setCurrentAssetType(assetType);
        setCurrentAssetIndex(assetIndex);
        setCurrentAssetData(assetData);

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: 'environment',
                    width: { ideal: 1920 },
                    height: { ideal: 1080 }
                }
            });

            setIsCameraOpen(true);

            // Wait for the next tick to ensure the video element is rendered
            await new Promise(resolve => setTimeout(resolve, 0));

            // More robust video initialization
            const initializeVideo = async () => {
                if (!videoRef.current) {
                    throw new Error('Video element not available');
                }

                videoRef.current.srcObject = stream;

                // Wait for the video to be ready
                return new Promise((resolve, reject) => {
                    const video = videoRef.current;

                    const onLoadedMetadata = () => {
                        video.removeEventListener('loadedmetadata', onLoadedMetadata);
                        video.removeEventListener('error', onError);
                        resolve();
                    };

                    const onError = (error) => {
                        video.removeEventListener('loadedmetadata', onLoadedMetadata);
                        video.removeEventListener('error', onError);
                        reject(new Error('Failed to load video: ' + error.message));
                    };

                    video.addEventListener('loadedmetadata', onLoadedMetadata);
                    video.addEventListener('error', onError);

                    // Start loading the video
                    video.load();
                });
            };

            await initializeVideo();

            // Play the video
            try {
                await videoRef.current.play();
            } catch (playError) {
                console.warn('Auto-play failed:', playError);
                // Auto-play might be blocked, but video is still ready for manual interaction
            }

        } catch (error) {
            console.error('Camera initialization error:', error);
            showAlert("Camera Error", "Unable to access camera: " + error.message, "error");

            // Clean up on error
            setIsCameraOpen(false);
            setCurrentAssetType(null);
            setCurrentAssetIndex(null);
            setCurrentAssetData(null);
        }
    };
    
    const capturePhoto = async () => {
        if (!videoRef.current || !canvasRef.current) return;

        const canvas = canvasRef.current;
        const video = videoRef.current;

        if (video.videoWidth === 0 || video.videoHeight === 0) {
            showAlert("Camera Error", "Video not ready. Please try again.", "error");
            return;
        }
 
        const ctx = canvas.getContext("2d");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        // Draw the camera frame
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        // Helper to sanitize values
        const safe = (v) => (v ?? "").toString().trim() || "N/A";

        // Pull ONLY from landAcquisition
        const meta = {
            surveyNo: safe(landAcquisition?.survey_no),
            district: safe(landAcquisition?.dist_name),
            taluka: safe(landAcquisition?.taluka_name),
            village: safe(landAcquisition?.village_name),
            ownerName: safe(landAcquisition?.landholder_name),
            proposal: safe(
                landAcquisition?.proposal_id 
            ),
            projectName: safe(
                landAcquisition?.project_name
            ),
            dateTime: new Date().toLocaleString("en-IN"),
        };

        // Draw readable bottom banner with lines
        const drawBanner = (lines) => {
            const padX = Math.round(canvas.width * 0.02);
            const padY = Math.round(canvas.height * 0.02);
            const fontSize = Math.max(14, Math.round(canvas.width * 0.018));
            const lineH = Math.round(fontSize * 1.4);

            ctx.save();
            ctx.font = `${fontSize}px sans-serif`;
            ctx.textBaseline = "top";

            const bannerH = lines.length * lineH + padY * 2;
            ctx.fillStyle = "rgba(0,0,0,0.55)";
            ctx.fillRect(0, canvas.height - bannerH, canvas.width, bannerH);

            ctx.fillStyle = "#fff";
            ctx.shadowColor = "rgba(0,0,0,0.9)";
            ctx.shadowBlur = 6;

            let y = canvas.height - bannerH + padY;
            for (const line of lines) {
                ctx.fillText(line, padX, y);
                y += lineH;
            }
            ctx.restore();
        };

        // Finalize common flow with enhanced metadata
        const finalizeWithBlob = async ({ blob, latitude, longitude, accuracy }) => {
            const timestamp = new Date().toISOString();

            // Create enhanced photo data with all metadata
            const photoData = {
                id: Date.now(),
                blob,
                url: URL.createObjectURL(blob),
                latitude: latitude ?? null,
                longitude: longitude ?? null,
                timestamp,
                accuracy,
                assetType: currentAssetType,
                assetIndex: currentAssetIndex,
                metadata: {
                    surveyNo: meta.surveyNo,
                    district: meta.district,
                    taluka: meta.taluka,
                    village: meta.village,
                    ownerName: meta.ownerName,
                    proposal: meta.proposal,
                    projectName: meta.projectName,
                    captureTime: meta.dateTime
                }
            };

            const photoKey = `${currentAssetType}_${currentAssetIndex}`;
            setCapturedPhotos((prev) => ({ ...prev, [photoKey]: photoData }));

            if (latitude != null && longitude != null) {
                showAlert(
                    "Photo Captured",
                    `Photo captured with GPS: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`,
                    "success"
                );
            } else {
                showAlert("Photo Captured", "Photo captured without GPS coordinates", "warning");
            }

            closeCamera();
        };

        // Try GPS with enhanced error handling
        try {
            const position = await new Promise((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(resolve, reject, {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 60000,
                });
            });

            const { latitude, longitude, accuracy } = position.coords;

            const lines = [
                `Lat: ${latitude.toFixed(6)}  Long: ${longitude.toFixed(6)}  ±${Math.round(accuracy)}m`,
                `Survey No: ${meta.surveyNo}`,
                `District: ${meta.district}   Taluka: ${meta.taluka}   Village: ${meta.village}`,
                `Landowner: ${meta.ownerName}`,
                `Proposal: ${meta.proposal}   Project: ${meta.projectName}`,
                `Date/Time: ${meta.dateTime}`,
            ];
            drawBanner(lines);

            canvas.toBlob(async (blob) => {
                if (!blob) {
                    showAlert("Camera Error", "Failed to capture image", "error");
                    return;
                }
                await finalizeWithBlob({ blob, latitude, longitude, accuracy });
            }, "image/jpeg", 0.85); // Slightly higher quality for API storage

        } catch (err) {
            console.error("GPS Error:", err);

            const lines = [
                `Lat: N/A  Long: N/A`,
                `Survey No: ${meta.surveyNo}`,
                `District: ${meta.district}   Taluka: ${meta.taluka}   Village: ${meta.village}`,
                `Landowner: ${meta.ownerName}`,
                `Proposal: ${meta.proposal}   Project: ${meta.projectName}`,
                `Date/Time: ${meta.dateTime}`,
            ];
            drawBanner(lines);

            canvas.toBlob(async (blob) => {
                if (!blob) {
                    showAlert("Camera Error", "Failed to capture image", "error");
                    return;
                }
                await finalizeWithBlob({ blob, latitude: null, longitude: null, accuracy: null });
            }, "image/jpeg", 0.85);
        }
    };

    const closeCamera = () => {
        try {
            if (videoRef.current && videoRef.current.srcObject) {
                const tracks = videoRef.current.srcObject.getTracks();
                tracks.forEach(track => {
                    track.stop();
                });
                videoRef.current.srcObject = null;
                videoRef.current.load(); // Reset the video element
            }
        } catch (error) {
            console.error('Error closing camera:', error);
        } finally {
            setIsCameraOpen(false);
            setCurrentAssetType(null);
            setCurrentAssetIndex(null);
            setCurrentAssetData(null);
        }
    };

    // ===================================================================
    // FETCH TREE TYPES
    // ===================================================================

    useEffect(() => {
        fetchTreeTypes();
    }, []);

    const fetchTreeTypes = async () => {
        try {
            setLoading(true);
            const data = await apiService.get('/getTreeType');

            if (data.status === 200) {
                const activeTreeTypes = data.data.filter(type => type.is_active);
                setTreeTypes(activeTreeTypes);

                const categories = [...new Set(activeTreeTypes.map(type => type.tree_type_name1))].filter(Boolean);
                setTreeCategories(categories);
            } else {
                setError('Failed to fetch tree types');
            }
        } catch (err) {
            setError('Error fetching tree types: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    // ===================================================================
    // MAIN FORM HANDLER
    // ===================================================================

    /**
     * Updated main form handler that saves assets individually
     */
    const handleAddOwner = async () => {
        // Validate areas before proceeding
        const validationErrors = validateAreas();

        if (validationErrors.length > 0) {
            // Show validation errors
            const errorMessage = "Please fix the following area validation errors:\n\n" +
                validationErrors.map((error, index) => `${index + 1}. ${error}`).join('\n');

            showAlert('Validation Error', errorMessage, 'error');

            // Scroll to the first error field (optional)
            const firstErrorField = document.querySelector('.border-red-500');
            if (firstErrorField) {
                firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
                firstErrorField.focus();
            }

            return; // Stop execution if validation fails
        }
        setLoading(true);

        try {
            console.log('Creating owner measurement...');
            const ownerId = await createOwnerMeasurement();
            console.log('Owner created with ID:', ownerId);

            handleFieldChange('land_owner_id', ownerId);

            // Save all assets individually after owner creation
            console.log('Saving assets...');

            // Save trees
            if (newOwner.tree_entries && newOwner.tree_entries.length > 0) {
                for (let i = 0; i < newOwner.tree_entries.length; i++) {
                    const entry = newOwner.tree_entries[i];
                    if (!entry.saved) {
                        await saveTree(entry, i, ownerId);
                    }
                }
            }

            // Save properties
            const propertyHandlers = [
                { entries: newOwner.home_entries, handler: homeHandlers },
                { entries: newOwner.tinshed_entries, handler: tinShedHandlers },
                { entries: newOwner.wirefence_entries, handler: wireFenceHandlers },
                { entries: newOwner.farmpond_entries, handler: farmPondHandlers },
                { entries: newOwner.well_entries, handler: wellHandlers },
                { entries: newOwner.borewell_entries, handler: borewellHandlers },
                { entries: newOwner.pipeline_entries, handler: pipelineHandlers },
                { entries: newOwner.other_entries, handler: otherHandlers },
            ];

            for (const { entries, handler } of propertyHandlers) {
                if (entries && entries.length > 0) {
                    for (let i = 0; i < entries.length; i++) {
                        const entry = entries[i];
                        if (!entry.saved) {
                            await handler.save(entry, i, ownerId);
                        }
                    }
                }
            }

            console.log('All assets saved successfully');

            resetForm();
            setShowAddOwner(false);
            setSelectedMeasurement(null);

            if (onRefresh) {
                await onRefresh();
            }

            if (onClose) onClose();

            showAlert('Success', 'Occupant and all assets added successfully!', 'success');

            if (typeof onBack === 'function') {
                onBack();
            }

        } catch (error) {
            console.error('Error in handleAddOwner:', error);
            showAlert('Error', `Failed to add owner: ${error.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setNewOwner({
            tree_entries: [],
            home_entries: [],
            tinshed_entries: [],
            wirefence_entries: [],
            farmpond_entries: [],
            well_entries: [],
            borewell_entries: [],
            pipeline_entries: [],
            other_entries: [],
            remarks: '',
            justification: ''
        });

        setSelectedImage(null);
        setSelectedVideo(null);
        setImagePreview(null);
        setVideoPreview(null);
        setUploadedDocuments([]);
        setUploadProgress(0);
    };

    // ===================================================================
    // UI EFFECTS AND INITIALIZATION
    // ===================================================================

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        if (!newOwner.tree_entries) {
            handleFieldChange('tree_entries', []);
        }
        if (!newOwner.home_entries) handleFieldChange('home_entries', []);
        if (!newOwner.tinshed_entries) handleFieldChange('tinshed_entries', []);
        if (!newOwner.wirefence_entries) handleFieldChange('wirefence_entries', []);
        if (!newOwner.farmpond_entries) handleFieldChange('farmpond_entries', []);
        if (!newOwner.well_entries) handleFieldChange('well_entries', []);
        if (!newOwner.borewell_entries) handleFieldChange('borewell_entries', []);
        if (!newOwner.pipeline_entries) handleFieldChange('pipeline_entries', []);
        if (!newOwner.other_entries) handleFieldChange('other_entries', []);
    }, []);

    useEffect(() => {
        if (landAcquisition?.landholder_name) {
            setNewOwner(prev => ({
                ...prev,
                owner_name: landAcquisition.landholder_name,
                account_holder_name: landAcquisition.landholder_name,
                account_number: '',
                ifsc_code: '',
            }));
        }
    }, [landAcquisition]);

    // ===================================================================
    // UI HELPER FUNCTIONS
    // ===================================================================

    const renderFieldWithError = (field, children, isValid = false) => {
        const hasError = validationErrors[field];
        const hasValue = newOwner[field];
        const showSuccess = hasValue && !hasError;

        return (
            <div className="space-y-1 relative">
                {children}
                {hasError && (
                    <div className="flex items-center text-red-500 text-xs">
                        <AlertCircle className="h-3 w-3 mr-1 flex-shrink-0" />
                        {hasError}
                    </div>
                )}
                {showSuccess && (
                    <div className="flex items-center text-green-500 text-xs">
                        <CheckCircle className="h-3 w-3 mr-1 flex-shrink-0" />
                        Valid
                    </div>
                )}
            </div>
        );
    };

    // Step indicator component
    const StepIndicator = ({ step, title, isActive }) => {
        const stepInfo = STEP_DEFINITIONS[step];
        const Icon = stepInfo?.icon || MapPin;

        return (
            <div
                className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${isActive ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50'
                    }`}
                onClick={() => setCurrentStep(step)}
            >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isActive ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'
                    }`}>
                    <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium truncate ${isActive ? 'text-blue-800' : 'text-gray-600'}`}>
                        {title}
                    </div>
                </div>
            </div>
        );
    };

    /**
     * Renders asset entry with universal View button
     */
    const renderAssetEntry = (entry, index, assetType, config) => {
        const { renderFields, handlers } = config;

        // Map display asset type to camera asset type for photo key consistency
        let cameraAssetType;
        switch (assetType) {
            case "trees":
                cameraAssetType = "trees";
                break;
            case "homes":
                cameraAssetType = "homes";
                break;
            case "tinsheds":
                cameraAssetType = "tinsheds";
                break;
            case "wirefences":
                cameraAssetType = "wirefences";
                break;
            case "farmponds":
                cameraAssetType = "farmponds";
                break;
            case "wells":
                cameraAssetType = "wells";
                break;
            case "borewells":
                cameraAssetType = "borewells";
                break;
            case "pipelines":
                cameraAssetType = "pipelines";
                break;
            case "others":
                cameraAssetType = "others";
                break;
            default:
                cameraAssetType = assetType;
        }

        const photoKey = `${cameraAssetType}_${index}`;
        const hasPhoto = capturedPhotos[photoKey] || existingPhotos[photoKey];

        // Debug logging
        console.log(`Asset: ${assetType}, CameraType: ${cameraAssetType}, Index: ${index}, PhotoKey: ${photoKey}, HasPhoto: ${!!hasPhoto}`);

        return (
            <div key={index} className="border border-gray-200 rounded-lg p-3 space-y-2">
                {renderFields(entry, index, handlers)}

                {/* Compact action buttons with universal View button */}
                <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                    <div className="flex items-center gap-2">
                        <SmallButton
                            variant="secondary"
                            onClick={() => {
                                console.log(`Opening camera for ${assetType} -> ${cameraAssetType}`);
                                openCamera(cameraAssetType, index, entry);
                            }}
                        >
                            <Camera className="h-4 w-4" />
                        </SmallButton>

                        <SmallButton
                            variant="secondary"
                            onClick={getCurrentLocation}
                            disabled={isGettingLocation}
                        >
                            {isGettingLocation ? <Loader2 className="h-4 w-4 animate-spin" /> : <Navigation className="h-4 w-4" />}
                        </SmallButton>

                        {/* UNIVERSAL VIEW BUTTON - Works for all asset types */}
                        <SmallButton
                            variant="secondary"
                            onClick={() => {
                                const photo = capturedPhotos[photoKey] || existingPhotos[photoKey];
                                if (photo) setPreviewFile(photo);
                            }}
                            disabled={!hasPhoto}
                            title={hasPhoto ? "View photo" : "No photo available"}
                        >
                            <Eye className="h-4 w-4" />
                        </SmallButton>

                        {hasPhoto && (
                            <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                                <CheckCircle className="h-3 w-3" />
                                Photo
                            </div>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        {!entry.saved && (
                            <SmallButton
                                variant="success"
                                onClick={() => handlers.save(entry, index)}
                                disabled={saving}
                            >
                                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                            </SmallButton>
                        )}
                        <SmallButton
                            variant="danger"
                            onClick={() => handlers.remove(index)}
                        >
                            <Trash2 className="h-4 w-4" />
                        </SmallButton>
                    </div>
                </div>

                {entry.saved && (
                    <div className="flex items-center gap-2 text-xs text-green-600 pt-1">
                        <CheckCircle className="h-3 w-3" />
                        Saved to database
                    </div>
                )}
            </div>
        );
    };

    // ===================================================================
    // STEP CONTENT RENDERERS
    // ===================================================================

    const renderPropertyOverview = () => (
        <div className="space-y-4">
            {/* Compact header */}
            <div className="flex items-center gap-3 pb-3 border-b">
                <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                    <MapPin className="h-4 w-4 text-orange-600" />
                </div>
                <div>
                    <h4 className="text-lg font-semibold">Property Overview</h4>
                    <p className="text-sm text-gray-500">Land details and occupant information</p>
                </div>
            </div>

            {/* Area Information */}
            {/* <SectionCard>
                <h5 className="text-sm font-medium mb-2">Area Proposal</h5>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                    <div className="p-2 bg-gray-50 rounded">
                        <div className="text-gray-500">Cultivable</div>
                        <div className="font-medium">{landAcquisition?.irrigable_area || 0}</div>
                    </div>
                    <div className="p-2 bg-gray-50 rounded">
                        <div className="text-gray-500">Non-Cultivable</div>
                        <div className="font-medium">{landAcquisition?.non_irrigable_area || 0}</div>
                    </div>
                    <div className="p-2 bg-gray-50 rounded">
                        <div className="text-gray-500">Total</div>
                        <div className="font-medium">{landAcquisition?.owner_total_area || 0}</div>
                    </div>
                    <div className="p-2 bg-gray-50 rounded">
                        <div className="text-gray-500">Acquired</div>
                        <div className="font-medium">{landAcquisition?.aqr_area || 0}</div>
                    </div>
                </div>
            </SectionCard> */}

            {/* Area According to 7/12 */}
            <SectionCard>
                <h5 className="text-sm font-medium mb-3">Area According to 7/12</h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                    <Field label="Cultivable">
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={newOwner.owner_7_12_irrigable_area || ""}
                            onChange={(e) => handleFieldChange("owner_7_12_irrigable_area", e.target.value)}
                            className="w-full h-9 px-3 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-400 focus:outline-none"
                            placeholder="Enter cultivable"
                        />
                    </Field>

                    <Field label="Non-Cultivable">
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={newOwner.owner_7_12_non_irrigable_area || ""}
                            onChange={(e) => handleFieldChange("owner_7_12_non_irrigable_area", e.target.value)}
                            className="w-full h-9 px-3 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-400 focus:outline-none"
                            placeholder="Enter non-cultivable"
                        />
                    </Field>

                    <Field label="Total">
                        <input
                            type="number"
                            step="0.01"
                            disabled
                            value={
                                (parseFloat(newOwner.owner_7_12_irrigable_area || 0) || 0) +
                                (parseFloat(newOwner.owner_7_12_non_irrigable_area || 0) || 0)
                            }
                            className="w-full h-9 px-3 text-sm rounded-lg border border-gray-300 bg-gray-100 text-gray-700 cursor-not-allowed"
                            placeholder="Auto total"
                        />
                    </Field>
                </div>
            </SectionCard>

            {/* Area According to JMR */}
            <SectionCard>
                <h5 className="text-sm font-medium mb-3">Area According to JMR</h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                    <Field label="Cultivable">
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={newOwner.owner_irrigable_area || ""}
                            onChange={(e) => {
                                const newValue = e.target.value;
                                const total7_12 = (parseFloat(newOwner.owner_7_12_irrigable_area || 0) || 0) +
                                    (parseFloat(newOwner.owner_7_12_non_irrigable_area || 0) || 0);
                                const currentNonCultivable = parseFloat(newOwner.owner_non_irrigable_area || 0) || 0;
                                const newCultivable = parseFloat(newValue || 0) || 0;

                                // Check if sum would exceed 7/12 total
                                if (newCultivable + currentNonCultivable <= total7_12) {
                                    handleFieldChange("owner_irrigable_area", newValue);
                                }
                            }}
                            className={`w-full h-9 px-3 text-sm rounded-lg border focus:ring-2 focus:ring-blue-400 focus:outline-none ${(() => {
                                const total7_12 = (parseFloat(newOwner.owner_7_12_irrigable_area || 0) || 0) +
                                    (parseFloat(newOwner.owner_7_12_non_irrigable_area || 0) || 0);
                                const jmrTotal = (parseFloat(newOwner.owner_irrigable_area || 0) || 0) +
                                    (parseFloat(newOwner.owner_non_irrigable_area || 0) || 0);
                                return jmrTotal > total7_12 ? 'border-red-500 bg-red-50' : 'border-gray-300';
                            })()}`}
                            placeholder="Enter cultivable area"
                        />
                    </Field>

                    <Field label="Non-Cultivable">
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={newOwner.owner_non_irrigable_area || ""}
                            onChange={(e) => {
                                const newValue = e.target.value;
                                const total7_12 = (parseFloat(newOwner.owner_7_12_irrigable_area || 0) || 0) +
                                    (parseFloat(newOwner.owner_7_12_non_irrigable_area || 0) || 0);
                                const currentCultivable = parseFloat(newOwner.owner_irrigable_area || 0) || 0;
                                const newNonCultivable = parseFloat(newValue || 0) || 0;

                                // Check if sum would exceed 7/12 total
                                if (currentCultivable + newNonCultivable <= total7_12) {
                                    handleFieldChange("owner_non_irrigable_area", newValue);
                                }
                            }}
                            className={`w-full h-9 px-3 text-sm rounded-lg border focus:ring-2 focus:ring-blue-400 focus:outline-none ${(() => {
                                const total7_12 = (parseFloat(newOwner.owner_7_12_irrigable_area || 0) || 0) +
                                    (parseFloat(newOwner.owner_7_12_non_irrigable_area || 0) || 0);
                                const jmrTotal = (parseFloat(newOwner.owner_irrigable_area || 0) || 0) +
                                    (parseFloat(newOwner.owner_non_irrigable_area || 0) || 0);
                                return jmrTotal > total7_12 ? 'border-red-500 bg-red-50' : 'border-gray-300';
                            })()}`}
                            placeholder="Enter non-cultivable area"
                        />
                    </Field>

                    <Field label="Total">
                        <input
                            type="number"
                            step="0.01"
                            disabled
                            value={
                                (parseFloat(newOwner.owner_irrigable_area || 0) || 0) +
                                (parseFloat(newOwner.owner_non_irrigable_area || 0) || 0)
                            }
                            className={`w-full h-9 px-3 text-sm rounded-lg border border-gray-300 bg-gray-100 text-gray-700 cursor-not-allowed ${(() => {
                                const total7_12 = (parseFloat(newOwner.owner_7_12_irrigable_area || 0) || 0) +
                                    (parseFloat(newOwner.owner_7_12_non_irrigable_area || 0) || 0);
                                const jmrTotal = (parseFloat(newOwner.owner_irrigable_area || 0) || 0) +
                                    (parseFloat(newOwner.owner_non_irrigable_area || 0) || 0);
                                return jmrTotal > total7_12 ? 'bg-red-50 border-red-300' : 'bg-gray-100';
                            })()}`}
                            placeholder="Auto-calculated total"
                        />
                    </Field>
                </div>

                {/* Validation Message */}
                {(() => {
                    const total7_12 = (parseFloat(newOwner.owner_7_12_irrigable_area || 0) || 0) +
                        (parseFloat(newOwner.owner_7_12_non_irrigable_area || 0) || 0);
                    const jmrTotal = (parseFloat(newOwner.owner_irrigable_area || 0) || 0) +
                        (parseFloat(newOwner.owner_non_irrigable_area || 0) || 0);
                    const remaining = total7_12 - jmrTotal;

                    if (jmrTotal > total7_12) {
                        return (
                            <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-lg">
                                <p className="text-xs text-red-600">
                                    ⚠️ JMR total ({jmrTotal.toFixed(2)}) exceeds 7/12 total ({total7_12.toFixed(2)}) by {(jmrTotal - total7_12).toFixed(2)}
                                </p>
                            </div>
                        );
                    } else if (jmrTotal < total7_12 && jmrTotal > 0) {
                        return (
                            <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="text-xs text-blue-600">
                                    ℹ️ Remaining area to allocate: {remaining.toFixed(2)} (Total 7/12: {total7_12.toFixed(2)}, JMR Total: {jmrTotal.toFixed(2)})
                                </p>
                            </div>
                        );
                    } else if (jmrTotal === total7_12 && total7_12 > 0) {
                        return (
                            <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded-lg">
                                <p className="text-xs text-green-600">
                                    ✓ JMR total matches 7/12 total perfectly ({total7_12.toFixed(2)})
                                </p>
                            </div>
                        );
                    }
                    return null;
                })()}
            </SectionCard>

            {/* Occupant Information */}
            {/* <SectionCard>
                <h5 className="text-sm font-medium mb-3">Occupant Information</h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {renderFieldWithError(
                        "owner_name",
                        <Field label="Occupant Name *" error={validationErrors.owner_name}>
                            <input
                                type="text"
                                value={newOwner.owner_name || ""}
                                onChange={(e) => handleFieldChange("owner_name", e.target.value)}
                                className="w-full h-9 px-3 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-400 focus:outline-none"
                                placeholder="Enter occupant name"
                            />
                        </Field>
                    )}

                    <Field label="Contact Number">
                        <input
                            type="tel"
                            value={newOwner.owner_contact || ""}
                            onChange={(e) => handleFieldChange("owner_contact", e.target.value)}
                            className="w-full h-9 px-3 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-400 focus:outline-none"
                            placeholder="Enter contact number"
                            maxLength="15"
                        />
                    </Field>

                    <Field label="Email Address">
                        <input
                            type="email"
                            value={newOwner.owner_email || ""}
                            onChange={(e) => handleFieldChange("owner_email", e.target.value)}
                            className="w-full h-9 px-3 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-400 focus:outline-none"
                            placeholder="example@email.com"
                        />
                    </Field>

                    <Field label="Nature of Acquisition">
                        <input
                            type="text"
                            value={newOwner.nature_of_owner || ""}
                            onChange={(e) => handleFieldChange("nature_of_owner", e.target.value)}
                            className="w-full h-9 px-3 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-400 focus:outline-none"
                            placeholder="Enter acquisition nature"
                        />
                    </Field>

                    <div className="md:col-span-2">
                        {renderFieldWithError(
                            "owner_address",
                            <Field label="Complete Address *" error={validationErrors.owner_address}>
                                <textarea
                                    value={newOwner.owner_address || ""}
                                    onChange={(e) => handleFieldChange("owner_address", e.target.value)}
                                    className="w-full h-16 px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-400 focus:outline-none resize-none"
                                    placeholder="Enter complete address with postal code"
                                />
                            </Field>
                        )}
                    </div>
                </div>
            </SectionCard> */}

            {/* Assets Summary */}
            <SectionCard>
                <h5 className="text-sm font-medium mb-3">Assets Summary</h5>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 text-xs">
                    <div className="p-2 bg-green-50 rounded cursor-pointer hover:bg-green-100 transition-colors" onClick={() => setCurrentStep(2)}>
                        <div className="flex items-center gap-2">
                            <TreePine className="h-4 w-4 text-green-600" />
                            <span className="text-green-800">Trees</span>
                        </div>
                        <div className="font-medium text-green-900">
                            {newOwner.tree_entries?.reduce((sum, entry) => sum + (parseInt(entry.count) || 0), 0) || 0}
                        </div>
                    </div>

                    <div className="p-2 bg-indigo-50 rounded cursor-pointer hover:bg-indigo-100 transition-colors" onClick={() => setCurrentStep(3)}>
                        <div className="flex items-center gap-2">
                            <Home className="h-4 w-4 text-indigo-600" />
                            <span className="text-indigo-800">Homes</span>
                        </div>
                        <div className="font-medium text-indigo-900">{newOwner.home_entries?.length || 0}</div>
                    </div>

                    <div className="p-2 bg-blue-50 rounded cursor-pointer hover:bg-blue-100 transition-colors" onClick={() => setCurrentStep(4)}>
                        <div className="flex items-center gap-2">
                            <Factory className="h-4 w-4 text-blue-600" />
                            <span className="text-blue-800">Sheds</span>
                        </div>
                        <div className="font-medium text-blue-900">{newOwner.tinshed_entries?.length || 0}</div>
                    </div>

                    <div className="p-2 bg-cyan-50 rounded cursor-pointer hover:bg-cyan-100 transition-colors" onClick={() => setCurrentStep(7)}>
                        <div className="flex items-center gap-2">
                            <Droplets className="h-4 w-4 text-cyan-600" />
                            <span className="text-cyan-800">Wells</span>
                        </div>
                        <div className="font-medium text-cyan-900">{newOwner.well_entries?.length || 0}</div>
                    </div>

                    <div className="p-2 bg-purple-50 rounded cursor-pointer hover:bg-purple-100 transition-colors" onClick={() => setCurrentStep(5)}>
                        <div className="flex items-center gap-2">
                            <Fence className="h-4 w-4 text-purple-600" />
                            <span className="text-purple-800">Fencing</span>
                        </div>
                        <div className="font-medium text-purple-900">{newOwner.wirefence_entries?.length || 0}</div>
                    </div>

                    <div className="p-2 bg-blue-50 rounded cursor-pointer hover:bg-blue-100 transition-colors" onClick={() => setCurrentStep(6)}>
                        <div className="flex items-center gap-2">
                            <Waves className="h-4 w-4 text-blue-600" />
                            <span className="text-blue-800">Ponds</span>
                        </div>
                        <div className="font-medium text-blue-900">{newOwner.farmpond_entries?.length || 0}</div>
                    </div>

                    <div className="p-2 bg-blue-50 rounded cursor-pointer hover:bg-blue-100 transition-colors" onClick={() => setCurrentStep(8)}>
                        <div className="flex items-center gap-2">
                            <Zap className="h-4 w-4 text-blue-600" />
                            <span className="text-blue-800">Borewells</span>
                        </div>
                        <div className="font-medium text-blue-900">{newOwner.borewell_entries?.length || 0}</div>
                    </div>

                    <div className="p-2 bg-gray-50 rounded cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => setCurrentStep(10)}>
                        <div className="flex items-center gap-2">
                            <Package className="h-4 w-4 text-gray-600" />
                            <span className="text-gray-800">Others</span>
                        </div>
                        <div className="font-medium text-gray-900">{newOwner.other_entries?.length || 0}</div>
                    </div>
                </div>
            </SectionCard>
        </div>
    );

    const renderTreesManagement = () => (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                        <TreePine className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                        <h4 className="text-lg font-semibold">Trees Management</h4>
                        <p className="text-sm text-gray-500">Add and manage tree inventory</p>
                    </div>
                </div>
                <SmallButton variant="success" onClick={addTreeEntry}>
                    <Plus className="h-4 w-4" />
                    Add Tree
                </SmallButton>
            </div>

            <SectionCard>
                {(!newOwner.tree_entries || newOwner.tree_entries.length === 0) ? (
                    <div className="text-center py-8">
                        <TreePine className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-500 mb-3">No trees added yet</p>
                        <SmallButton variant="success" onClick={addTreeEntry}>
                            <Plus className="h-4 w-4" />
                            Add First Tree
                        </SmallButton>
                    </div>
                ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto" ref={treeContainerRef}>
                        {newOwner.tree_entries.map((entry, index) =>
                            renderAssetEntry(entry, index, "trees", {
                                renderFields: (entry, index, handlers) => (
                                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                                        <Field label="Count *">
                                            <input
                                                type="number"
                                                min="1"
                                                value={entry.count || ""}
                                                onChange={(e) => updateTreeEntry(index, "count", e.target.value)}
                                                disabled={entry.saved && editingTreeId !== entry.tree_id}
                                                className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-green-500 disabled:bg-gray-100"
                                                placeholder="Count"
                                            />
                                        </Field>

                                        <Field label="Category *">
                                            <select
                                                value={entry.category || ""}
                                                onChange={(e) => updateTreeEntry(index, "category", e.target.value)}
                                                disabled={entry.saved && editingTreeId !== entry.tree_id}
                                                className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-green-500 disabled:bg-gray-100"
                                            >
                                                <option value="">Select Category</option>
                                                {treeCategories.map((category) => (
                                                    <option key={category} value={category}>
                                                        {category}
                                                    </option>
                                                ))}
                                            </select>
                                        </Field>

                                        <Field label="Tree Type *">
                                            <select
                                                value={entry.type_id || ""}
                                                onChange={(e) => updateTreeEntry(index, "type_id", e.target.value)}
                                                disabled={(entry.saved && editingTreeId !== entry.tree_id) || !entry.category}
                                                className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-green-500 disabled:bg-gray-100"
                                            >
                                                <option value="">Select Type</option>
                                                {getTreesByCategory(entry.category).map((type) => (
                                                    <option key={type.tree_type_id} value={type.tree_type_id}>
                                                        {type.tree_type_name}
                                                    </option>
                                                ))}
                                                {/* <option value="other">Other</option> */}
                                            </select>
                                        </Field>

                                        {/* {entry.type_id === "other" && (
                                            <Field label="Custom Type *">
                                                <input
                                                    type="text"
                                                    value={entry.other_type || ""}
                                                    onChange={(e) => updateTreeEntry(index, "other_type", e.target.value)}
                                                    disabled={entry.saved && editingTreeId !== entry.tree_id}
                                                    className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-green-500 disabled:bg-gray-100"
                                                    placeholder="Enter custom type"
                                                />
                                            </Field>
                                        )} */}
                                    </div>
                                ),
                                handlers: { save: saveTree, remove: removeTreeEntry }
                            })
                        )}
                    </div>
                )}

                {/* Trees Summary */}
                {newOwner.tree_entries && newOwner.tree_entries.length > 0 && (
                    <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2">
                                <TreePine className="h-4 w-4 text-green-600" />
                                <span className="font-medium text-green-800">Trees Summary</span>
                            </div>
                            <div className="text-green-600">
                                Total: {newOwner.tree_entries.reduce((sum, entry) => sum + (parseInt(entry.count) || 0), 0)} trees |
                                Entries: {newOwner.tree_entries.length}
                            </div>
                        </div>
                    </div>
                )}
            </SectionCard>
        </div>
    );

    const renderHomesManagement = () => (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                        <Home className="h-4 w-4 text-indigo-600" />
                    </div>
                    <div>
                        <h4 className="text-lg font-semibold">Homes Management</h4>
                        <p className="text-sm text-gray-500">Manage residential structures</p>
                    </div>
                </div>
                <SmallButton variant="primary" onClick={addHomeEntry}>
                    <Plus className="h-4 w-4" />
                    Add Home
                </SmallButton>
            </div>

            <SectionCard>
                {(!newOwner.home_entries || newOwner.home_entries.length === 0) ? (
                    <div className="text-center py-8">
                        <Home className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-500 mb-3">No homes added yet</p>
                        <SmallButton variant="primary" onClick={addHomeEntry}>
                            <Plus className="h-4 w-4" />
                            Add First Home
                        </SmallButton>
                    </div>
                ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto" ref={homesContainerRef}>
                        {newOwner.home_entries.map((entry, index) =>
                            renderAssetEntry(entry, index, "homes", {
                                renderFields: (home, index) => {
                                    const floor = home.floors?.[0] || {};
                                    return (
                                        <div className="space-y-2">
                                            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                                                <Field label="Construction Type *">
                                                    <select
                                                        value={floor.construction_type || "Permanent House"}
                                                        onChange={(e) => updateHomeEntry(index, 0, "construction_type", e.target.value)}
                                                        disabled={home.saved}
                                                        className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100"
                                                    >
                                                        <option value="Permanent House">Permanent House</option>
                                                        <option value="Temporary House">RCC</option>
                                                        <option value="Other">Other</option>
                                                    </select>
                                                </Field>

                                                {floor.construction_type === "Other" ? (
                                                    <Field label="Custom Type *">
                                                        <input
                                                            type="text"
                                                            value={floor.custom_construction_type || ""}
                                                            onChange={(e) => updateHomeEntry(index, 0, "custom_construction_type", e.target.value)}
                                                            disabled={home.saved}
                                                            className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100"
                                                            placeholder="Custom type"
                                                        />
                                                    </Field>
                                                ) : (
                                                    <Field label="Floor Type *">
                                                        <select
                                                            value={floor.type || ""}
                                                            onChange={(e) => updateHomeEntry(index, 0, "type", e.target.value)}
                                                            disabled={home.saved}
                                                            className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100"
                                                        >
                                                            <option value="">Select Floor</option>
                                                            <option value="ground_floor">Ground Floor</option>
                                                            {floor.construction_type === "Permanent House" && (
                                                                <>
                                                                    <option value="first_floor">1st Floor</option>
                                                                    <option value="second_floor">2nd Floor</option>
                                                                </>
                                                            )}
                                                        </select>
                                                    </Field>
                                                )}

                                                <Field label="Length (m) *">
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                        value={floor.length || ""}
                                                        onChange={(e) => updateHomeEntry(index, 0, "length", e.target.value)}
                                                        disabled={home.saved}
                                                        className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100"
                                                        placeholder="Length"
                                                    />
                                                </Field>

                                                <Field label="Width (m) *">
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                        value={floor.width || ""}
                                                        onChange={(e) => updateHomeEntry(index, 0, "width", e.target.value)}
                                                        disabled={home.saved}
                                                        className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100"
                                                        placeholder="Width"
                                                    />
                                                </Field>
                                            </div>

                                            {floor.length && floor.width && (
                                                <div className="text-xs text-indigo-600 bg-indigo-50 p-2 rounded">
                                                    Total Area: {(parseFloat(floor.length) * parseFloat(floor.width)).toFixed(2)} m²
                                                </div>
                                            )}
                                        </div>
                                    );
                                },
                                handlers: homeHandlers
                            })
                        )}
                    </div>
                )}
            </SectionCard>
        </div>
    );

    // Generic asset management renderer
    const renderGenericAssetManagement = (assetKey, title, icon, color, handlers, entries, displayAssetType) => (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 bg-${color}-100 rounded-lg flex items-center justify-center`}>
                        {React.createElement(icon, { className: `h-4 w-4 text-${color}-600` })}
                    </div>
                    <div>
                        <h4 className="text-lg font-semibold">{title}</h4>
                        <p className="text-sm text-gray-500">Manage {title.toLowerCase()}</p>
                    </div>
                </div>
                <SmallButton variant="primary" onClick={handlers.add}>
                    <Plus className="h-4 w-4" />
                    Add {title.split(' ')[0]}
                </SmallButton>
            </div>

            <SectionCard>
                {(!entries || entries.length === 0) ? (
                    <div className="text-center py-8">
                        {React.createElement(icon, { className: "h-8 w-8 text-gray-400 mx-auto mb-2" })}
                        <p className="text-sm text-gray-500 mb-3">No {title.toLowerCase()} added yet</p>
                        <SmallButton variant="primary" onClick={handlers.add}>
                            <Plus className="h-4 w-4" />
                            Add First {title}
                        </SmallButton>
                    </div>
                ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                        {entries.map((entry, index) => renderAssetEntry(entry, index, displayAssetType || assetKey.replace('_entries', 's'), {
                            renderFields: (entry, index) => renderAssetFields(assetKey, entry, index, handlers),
                            handlers
                        }))}
                    </div>
                )}
            </SectionCard>
        </div>
    );

    // Asset field renderers for different types
    const renderAssetFields = (assetKey, entry, index, handlers) => {
        switch (assetKey) {
            case 'tinshed_entries':
                return (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <Field label="Length (m) *">
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={entry.length || ""}
                                onChange={(e) => handlers.update(index, 'length', e.target.value)}
                                disabled={entry.saved}
                                className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
                                placeholder="Length"
                            />
                        </Field>
                        <Field label="Width (m) *">
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={entry.width || ""}
                                onChange={(e) => handlers.update(index, 'width', e.target.value)}
                                disabled={entry.saved}
                                className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
                                placeholder="Width"
                            />
                        </Field>
                        {entry.length && entry.width && (
                            <div className="col-span-full text-xs text-blue-600 bg-blue-50 p-2 rounded">
                                Total Area: {(parseFloat(entry.length) * parseFloat(entry.width)).toFixed(2)} m²
                            </div>
                        )}
                    </div>
                );

            case 'wirefence_entries':
                const pipe = entry.pipes?.[0] || {};
                return (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <Field label="Length (m) *">
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={pipe.length || ""}
                                onChange={(e) => handlers.update(index, 'pipes[0].length', e.target.value)}
                                disabled={entry.saved}
                                className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-purple-500 disabled:bg-gray-100"
                                placeholder="Length"
                            />
                        </Field>
                        <Field label="Height (m)">
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={pipe.height || ""}
                                onChange={(e) => handlers.update(index, 'pipes[0].height', e.target.value)}
                                disabled={entry.saved}
                                className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-purple-500 disabled:bg-gray-100"
                                placeholder="Height"
                            />
                        </Field>
                        <div className="flex items-end">
                            <Field label="Area (m²)">
                                <div className="h-8 px-2 py-1 bg-gray-100 border border-gray-300 rounded text-sm text-gray-700">
                                    {pipe.length && pipe.height
                                        ? (parseFloat(pipe.length) * parseFloat(pipe.height)).toFixed(2)
                                        : "0.00"}
                                </div>
                            </Field>
                        </div>
                    </div>
                );

            case 'farmpond_entries':
                const dim = entry.dimensions?.[0] || {};
                return (
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                        <Field label="Length (m) *">
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={dim.length || ""}
                                onChange={(e) => handlers.update(index, 'dimensions[0].length', e.target.value)}
                                disabled={entry.saved}
                                className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
                                placeholder="Length"
                            />
                        </Field>
                        <Field label="Width (m) *">
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={dim.width || ""}
                                onChange={(e) => handlers.update(index, 'dimensions[0].width', e.target.value)}
                                disabled={entry.saved}
                                className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
                                placeholder="Width"
                            />
                        </Field>
                        <Field label="Depth (m) *">
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={dim.depth || ""}
                                onChange={(e) => handlers.update(index, 'dimensions[0].depth', e.target.value)}
                                disabled={entry.saved}
                                className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
                                placeholder="Depth"
                            />
                        </Field>
                        <div className="flex items-end">
                            <Field label="Volume (m³)">
                                <div className="h-8 px-2 py-1 bg-gray-100 border border-gray-300 rounded text-sm text-gray-700">
                                    {dim.length && dim.width && dim.depth
                                        ? (parseFloat(dim.length) * parseFloat(dim.width) * parseFloat(dim.depth)).toFixed(2)
                                        : "0.00"}
                                </div>
                            </Field>
                        </div>
                    </div>
                );

            case 'well_entries':
                return (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <Field label="Diameter (m) *">
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={entry.diameter || ""}
                                onChange={(e) => handlers.update(index, 'diameter', e.target.value)}
                                disabled={entry.saved}
                                className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-cyan-500 disabled:bg-gray-100"
                                placeholder="Diameter"
                            />
                        </Field>
                        <Field label="Depth (m) *">
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={entry.depth || ""}
                                onChange={(e) => handlers.update(index, 'depth', e.target.value)}
                                disabled={entry.saved}
                                className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-cyan-500 disabled:bg-gray-100"
                                placeholder="Depth"
                            />
                        </Field>
                    </div>
                );

            case 'borewell_entries':
                return (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">

                        <Field label="Depth (m) *">
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={entry.depth || ""}
                                onChange={(e) => handlers.update(index, 'depth', e.target.value)}
                                disabled={entry.saved}
                                className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
                                placeholder="Depth"
                            />
                        </Field>
                    </div>
                );

            case 'pipeline_entries':
                return (
                    <div className="grid grid-cols-2 gap-2">
                        <Field label="Length (m) *">
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={entry.length || ""}
                                onChange={(e) => handlers.update(index, 'length', e.target.value)}
                                disabled={entry.saved}
                                className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-sky-500 disabled:bg-gray-100"
                                placeholder="Length"
                            />
                        </Field>
                        <Field label="Diameter *">
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={entry.diameter || ""}
                                onChange={(e) => handlers.update(index, 'diameter', e.target.value)}
                                disabled={entry.saved}
                                className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-sky-500 disabled:bg-gray-100"
                                placeholder="Diameter"
                            />
                        </Field>
                    </div>
                );

            case 'other_entries':
                return (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <Field label="Asset Name *">
                            <input
                                type="text"
                                value={entry.name || ""}
                                onChange={(e) => handlers.update(index, 'name', e.target.value)}
                                disabled={entry.saved}
                                className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-gray-500 disabled:bg-gray-100"
                                placeholder="Asset name"
                            />
                        </Field>
                        <Field label="Description *">
                            <input
                                type="text"
                                step="0.01"
                                min="0"
                                value={entry.quantity || ""}
                                onChange={(e) => handlers.update(index, 'quantity', e.target.value)}
                                disabled={entry.saved}
                                className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-gray-500 disabled:bg-gray-100"
                                placeholder="Description"
                            />
                        </Field>


                    </div>
                );

            default:
                return <div className="text-sm text-gray-600">Asset #{index + 1}</div>;
        }
    };

    const renderLocationAndMedia = () => (
        <div className="space-y-4">
            <div className="flex items-center gap-3 pb-3 border-b">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Navigation className="h-4 w-4 text-purple-600" />
                </div>
                <div>
                    <h4 className="text-lg font-semibold">Location & Media</h4>
                    <p className="text-sm text-gray-500">GPS coordinates and media files</p>
                </div>
            </div>

            <SectionCard>
                <h5 className="text-sm font-medium mb-3">GPS Location</h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Latitude">
                        <div className="flex gap-2">
                            <input
                                type="number"
                                step="any"
                                value={newOwner.latitude || ""}
                                onChange={(e) => handleFieldChange("latitude", e.target.value)}
                                className="flex-1 h-9 px-3 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-400 focus:outline-none"
                                placeholder="Latitude"
                            />
                            <SmallButton
                                variant="success"
                                onClick={getCurrentLocation}
                                disabled={isGettingLocation}
                            >
                                {isGettingLocation ? <Loader2 className="h-4 w-4 animate-spin" /> : <Navigation className="h-4 w-4" />}
                            </SmallButton>
                        </div>
                    </Field>

                    <Field label="Longitude">
                        <div className="flex gap-2">
                            <input
                                type="number"
                                step="any"
                                value={newOwner.longitude || ""}
                                onChange={(e) => handleFieldChange("longitude", e.target.value)}
                                className="flex-1 h-9 px-3 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-400 focus:outline-none"
                                placeholder="Longitude"
                            />
                            <SmallButton
                                variant="secondary"
                                onClick={openMapView}
                                disabled={!newOwner.latitude || !newOwner.longitude}
                            >
                                <Eye className="h-4 w-4" />
                            </SmallButton>
                        </div>
                    </Field>
                </div>

                {newOwner.latitude && newOwner.longitude && (
                    <div className="mt-3 p-2 bg-green-50 rounded text-xs text-green-700">
                        <strong>Current Location:</strong> {newOwner.latitude}, {newOwner.longitude}
                    </div>
                )}
            </SectionCard>

            {/* Media Upload Section */}
            <SectionCard>
                <h5 className="text-sm font-medium mb-3">Property Media</h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Property Video">
                        <div className="space-y-2">
                            <label className="flex items-center justify-center w-full h-16 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                                <div className="flex flex-col items-center">
                                    <Video className="h-6 w-6 text-gray-400 mb-1" />
                                    <span className="text-xs text-gray-500">Upload Video</span>
                                </div>
                                <input
                                    type="file"
                                    accept=".mp4,.avi,.mov,.wmv,.flv,.webm"
                                    onChange={handleVideoSelect}
                                    className="hidden"
                                />
                            </label>
                            {selectedVideo && (
                                <div className="text-xs text-green-600">
                                    {selectedVideo.name} ({(selectedVideo.size / 1024 / 1024).toFixed(2)} MB)
                                </div>
                            )}
                        </div>
                    </Field>

                    <Field label="Property Image">
                        <div className="space-y-2">
                            <label className="flex items-center justify-center w-full h-16 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                                <div className="flex flex-col items-center">
                                    <Upload className="h-6 w-6 text-gray-400 mb-1" />
                                    <span className="text-xs text-gray-500">Upload Image</span>
                                </div>
                                <input
                                    type="file"
                                    accept=".jpg,.jpeg,.png,.gif,.bmp,.webp"
                                    onChange={handleImageSelect}
                                    className="hidden"
                                />
                            </label>
                            {selectedImage && (
                                <div className="text-xs text-green-600">
                                    {selectedImage.name} ({(selectedImage.size / 1024 / 1024).toFixed(2)} MB)
                                </div>
                            )}
                        </div>
                    </Field>
                </div>
            </SectionCard>
        </div>
    );

    const renderRemarksManagement = () => (
        <div className="space-y-4">
            <div className="flex items-center gap-3 pb-3 border-b">
                <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <FileText className="h-4 w-4 text-yellow-600" />
                </div>
                <div>
                    <h4 className="text-lg font-semibold">Remarks</h4>
                    <p className="text-sm text-gray-500">Additional remarks and justification</p>
                </div>
            </div>

            <SectionCard>
                <div className="space-y-4">
                    <Field label="Remark">
                        <textarea
                            value={newOwner.justification || ""}
                            onChange={(e) => handleFieldChange("justification", e.target.value)}
                            className="w-full h-24 px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-yellow-400 focus:outline-none resize-none"
                            placeholder="Enter any additional remarks about the property or measurement..."
                            rows="4"
                        />
                    </Field>

                    <Field label="Justification">
                        <textarea
                            value={newOwner.remarks || ""}
                            onChange={(e) => handleFieldChange("remarks", e.target.value)}
                            className="w-full h-24 px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-yellow-400 focus:outline-none resize-none"
                            placeholder="Enter justification for any special considerations or decisions made..."
                            rows="4"
                        />
                    </Field>


                </div>
            </SectionCard>
        </div>
    );

    const renderCurrentStepContent = () => {
        switch (currentStep) {
            case 1: return renderPropertyOverview();
            case 2: return renderTreesManagement();
            case 3: return renderHomesManagement();
            case 4: return renderGenericAssetManagement('tinshed_entries', 'Tin Sheds', Factory, 'blue', tinShedHandlers, newOwner.tinshed_entries, 'tinsheds');
            case 5: return renderGenericAssetManagement('wirefence_entries', 'Wire Fencing', Fence, 'purple', wireFenceHandlers, newOwner.wirefence_entries, 'wirefences');
            case 6: return renderGenericAssetManagement('farmpond_entries', 'Farm Ponds', Waves, 'blue', farmPondHandlers, newOwner.farmpond_entries, 'farmponds');
            case 7: return renderGenericAssetManagement('well_entries', 'Wells', Droplets, 'cyan', wellHandlers, newOwner.well_entries, 'wells');
            case 8: return renderGenericAssetManagement('borewell_entries', 'Borewells', Zap, 'blue', borewellHandlers, newOwner.borewell_entries, 'borewells');
            case 9: return renderGenericAssetManagement('pipeline_entries', 'Pipelines', Zap, 'sky', pipelineHandlers, newOwner.pipeline_entries, 'pipelines');
            case 10: return renderGenericAssetManagement('other_entries', 'Other Assets', Package, 'gray', otherHandlers, newOwner.other_entries, 'others');
            case 11: return renderLocationAndMedia();
            case 12: return renderRemarksManagement();
            default: return <div className="text-center py-8 text-gray-500">Step not found</div>;
        }
    };

    // ===================================================================
    // MAIN RENDER
    // ===================================================================

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-2">
            <div className={`bg-white rounded-xl shadow-2xl w-full max-h-[95vh] flex flex-col overflow-hidden ${isMobile ? 'max-w-full' : 'max-w-6xl'
                }`}>

                {/* Compact Header */}
                <div className="px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            {isMobile && (
                                <button
                                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                                    className="p-1 hover:bg-white/20 rounded transition-colors"
                                >
                                    <Menu className="h-4 w-4" />
                                </button>
                            )}
                            <div>
                                <h3 className="text-lg font-bold flex items-center">
                                    <Building2 className="h-5 w-5 mr-2" />
                                    Add Property Occupant
                                </h3>
                                <p className="text-blue-100 text-xs">
                                    Survey: {landAcquisition?.survey_no || 'N/A'} |
                                    Landholder: {
                                        landAcquisition?.landholder_name ||
                                        landAcquisition?.landHolderName ||
                                        landAcquisition?.owner_name ||
                                        landAcquisition?.ownerName ||
                                        newOwner?.owner_name ||
                                        prefillData?.landholder_name ||
                                        prefillData?.owner_name ||
                                        'N/A'
                                    }
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onCancel}
                            className="p-1 hover:bg-white/20 rounded transition-colors"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                <div className="flex flex-1 overflow-hidden">
                    {/* Compact Sidebar */}
                    <div className={`${isMobile
                        ? `fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-xl transform transition-transform ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
                        }`
                        : 'w-64 bg-gray-50 border-r'
                        } overflow-y-auto`}>

                        {isMobile && isMobileMenuOpen && (
                            <div className="p-3 border-b flex items-center justify-between">
                                <h4 className="font-medium text-gray-800">Navigation</h4>
                                <button
                                    onClick={() => setIsMobileMenuOpen(false)}
                                    className="p-1 hover:bg-gray-200 rounded transition-colors"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                        )}

                        <div className="p-3">
                            <h4 className="text-sm font-medium text-gray-800 mb-3">Steps</h4>
                            <div className="space-y-1">
                                {Object.entries(STEP_DEFINITIONS).map(([step, stepInfo]) => (
                                    <StepIndicator
                                        key={step}
                                        step={parseInt(step)}
                                        title={stepInfo.title}
                                        isActive={currentStep === parseInt(step)}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Mobile Overlay */}
                    {isMobile && isMobileMenuOpen && (
                        <div
                            className="fixed inset-0 bg-black/50 z-40"
                            onClick={() => setIsMobileMenuOpen(false)}
                        />
                    )}

                    {/* Main Content */}
                    <div className="flex-1 flex flex-col">
                        {/* Error Display */}
                        {error && (
                            <div className="mx-4 mt-3 p-2 bg-red-50 border-l-4 border-red-400 text-red-700 rounded-r text-sm flex items-center">
                                <AlertCircle className="h-4 w-4 mr-2" />
                                {error}
                            </div>
                        )}

                        {/* Content Area */}
                        <div className="flex-1 overflow-y-auto px-4 py-3">
                            {renderCurrentStepContent()}
                        </div>

                        {/* Compact Footer */}
                        <div className="border-t bg-gray-50 px-4 py-3">
                            <div className={`flex items-center ${isMobile ? 'flex-col-reverse gap-2' : 'justify-between'}`}>
                                <div className="text-xs text-gray-500">
                                    Step {currentStep} of {Object.keys(STEP_DEFINITIONS).length}
                                </div>

                                <div className={`flex gap-2 ${isMobile ? 'w-full' : ''}`}>
                                    <SmallButton
                                        variant="secondary"
                                        onClick={onCancel}
                                        disabled={loading}
                                        className={isMobile ? 'flex-1' : ''}
                                    >
                                        Cancel
                                    </SmallButton>
                                    <SmallButton
                                        variant="success"
                                        onClick={handleAddOwner}
                                        disabled={loading}
                                        className={isMobile ? 'flex-1' : ''}
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                Adding...
                                            </>
                                        ) : (
                                            <>
                                                <Save className="h-4 w-4" />
                                                Add Occupant
                                            </>
                                        )}
                                    </SmallButton>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Camera Modal */}
            {isCameraOpen && (
                <div className="fixed inset-0 z-[60] bg-black bg-opacity-95 flex flex-col">
                    <div className="flex items-center justify-between p-3 text-white">
                        <div>
                            <h2 className="text-lg font-semibold">
                                Camera - {currentAssetType?.charAt(0).toUpperCase() + currentAssetType?.slice(1)} #{currentAssetIndex + 1}
                            </h2>
                            <p className="text-sm opacity-75">Tap capture for photo with GPS</p>
                        </div>
                        <button
                            onClick={closeCamera}
                            className="p-2 hover:bg-white/20 rounded-full transition-colors"
                        >
                            <X className="h-6 w-6" />
                        </button>
                    </div>

                    {/* Video Preview */}
                    <div className="flex-1 flex items-center justify-center p-4">
                        <div className="relative w-full max-w-2xl">
                            <video
                                ref={videoRef}
                                autoPlay
                                playsInline
                                muted
                                className="w-full rounded-lg shadow-2xl"
                            />
                            <canvas ref={canvasRef} className="hidden" />

                            {/* GPS Status */}
                            <div className="absolute top-3 left-3 right-3 bg-black/50 text-white p-2 rounded-lg">
                                <div className="flex items-center justify-between text-sm">
                                    <div className="flex items-center gap-2">
                                        <MapPin className="h-4 w-4" />
                                        <span>GPS Ready</span>
                                    </div>
                                    <div className="opacity-75">
                                        {new Date().toLocaleTimeString()}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Capture Button */}
                    <div className="flex items-center justify-center p-6">
                        <button
                            onClick={capturePhoto}
                            className="w-16 h-16 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors shadow-lg"
                        >
                            <Camera className="h-8 w-8 text-gray-800" />
                        </button>
                    </div>
                </div>
            )}

            {/* Enhanced File Preview Modal */}
            {previewFile && (
                <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-2 sm:p-4">
                    <div className={`relative bg-white rounded-xl shadow-xl w-full flex flex-col overflow-hidden ${isMobile ? 'h-[90vh]' : 'max-w-5xl h-[90vh]'}`}>
                        {/* Header */}
                        <div className="flex justify-between items-center p-3 sm:p-4 border-b bg-gray-50">
                            <h2 className="text-base sm:text-lg font-semibold">
                                {previewFile.url ? "Captured Photo" :
                                    previewFile.type?.startsWith('video/') ? 'Video Preview' : 'File Preview'}
                            </h2>
                            <button onClick={() => setPreviewFile(null)} className="p-2 hover:bg-gray-100 rounded-full">
                                <X className="h-4 w-4 sm:h-5 sm:w-5" />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-auto bg-gray-100 flex items-center justify-center">
                            {previewFile.url ? (
                                // Handle captured photos
                                <div className="flex flex-col items-center space-y-4 p-4">
                                    <img
                                        src={previewFile.url}
                                        alt="Captured Photo"
                                        className="max-h-full max-w-full object-contain rounded-lg shadow-lg"
                                    />
                                    {previewFile.latitude && previewFile.longitude && (
                                        <div className="bg-green-100 px-4 py-2 rounded-lg text-sm">
                                            <div className="flex items-center gap-2">
                                                <MapPin className="h-4 w-4 text-green-600" />
                                                <span className="font-medium">GPS Coordinates:</span>
                                                <span>{previewFile.latitude.toFixed(6)}, {previewFile.longitude.toFixed(6)}</span>
                                            </div>
                                            <div className="text-xs text-gray-600 mt-1">
                                                Accuracy: ±{previewFile.accuracy?.toFixed(0)}m |
                                                Time: {new Date(previewFile.timestamp).toLocaleString()}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : previewFile.type?.startsWith('video/') ? (
                                <video
                                    src={URL.createObjectURL(previewFile)}
                                    controls
                                    className="max-w-full max-h-full"
                                    autoPlay={false}
                                />
                            ) : previewFile.type === "application/pdf" ? (
                                <iframe
                                    src={URL.createObjectURL(previewFile)}
                                    title="PDF Preview"
                                    className="w-full h-full"
                                />
                            ) : (
                                <img
                                    src={URL.createObjectURL(previewFile)}
                                    alt="Preview"
                                    className="max-h-full max-w-full object-contain"
                                />
                            )}
                        </div>

                        {/* Footer with file info */}
                        <div className="p-3 sm:p-4 border-t bg-gray-50">
                            <div className={`flex items-center text-xs sm:text-sm text-gray-600 ${isMobile ? 'flex-col gap-2' : 'justify-between'}`}>
                                <div>
                                    <span className="font-medium">File:</span> {previewFile.url ? "Captured Photo" :
                                        previewFile.name?.length > (isMobile ? 30 : 50)
                                            ? `${previewFile.name.substring(0, isMobile ? 30 : 50)}...`
                                            : previewFile.name}
                                </div>
                                {previewFile.blob && (
                                    <div>
                                        <span className="font-medium">Size:</span> {(previewFile.blob.size / 1024 / 1024).toFixed(2)} MB
                                    </div>
                                )}
                                {!previewFile.url && previewFile.size && (
                                    <div>
                                        <span className="font-medium">Size:</span> {(previewFile.size / 1024 / 1024).toFixed(2)} MB
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Alert Modal */}
            {showAlertModal && (
                <div className={`fixed z-[70] ${isMobile ? 'inset-x-2 top-4' : 'top-4 right-4'}`}>
                    <div className={`bg-white rounded-lg shadow-lg border-l-4 p-3 ${isMobile ? 'w-full' : 'max-w-sm'
                        } ${alertConfig.type === "error" ? "border-red-500" :
                            alertConfig.type === "warning" ? "border-yellow-500" :
                                alertConfig.type === "info" ? "border-blue-500" : "border-green-500"
                        }`}>
                        <div className="flex items-start">
                            <div className="flex-shrink-0">
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center ${alertConfig.type === "error" ? "bg-red-100" :
                                    alertConfig.type === "warning" ? "bg-yellow-100" :
                                        alertConfig.type === "info" ? "bg-blue-100" : "bg-green-100"
                                    }`}>
                                    <span className={`text-xs font-bold ${alertConfig.type === "error" ? "text-red-600" :
                                        alertConfig.type === "warning" ? "text-yellow-600" :
                                            alertConfig.type === "info" ? "text-blue-600" : "text-green-600"
                                        }`}>
                                        {alertConfig.type === "error" ? "✕" :
                                            alertConfig.type === "warning" ? "⚠" :
                                                alertConfig.type === "info" ? "ℹ" : "✓"}
                                    </span>
                                </div>
                            </div>
                            <div className="ml-2 flex-1">
                                <h3 className="text-sm font-medium text-gray-900">
                                    {alertConfig.title}
                                </h3>
                                <p className="text-xs text-gray-600 mt-1">
                                    {alertConfig.message}
                                </p>
                            </div>
                            <button
                                onClick={() => setShowAlertModal(false)}
                                className="ml-2 text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation Modal */}
            {showConfirmModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
                    <div className={`bg-white rounded-lg shadow-lg w-full mx-4 ${isMobile ? 'max-w-sm' : 'max-w-sm'}`}>
                        <div className="p-4">
                            <div className={`w-10 h-10 rounded-full mx-auto mb-3 flex items-center justify-center ${confirmConfig.type === "danger" ? "bg-red-100" :
                                confirmConfig.type === "warning" ? "bg-yellow-100" : "bg-blue-100"
                                }`}>
                                <span className="text-lg">
                                    {confirmConfig.type === "danger" ? "⚠" :
                                        confirmConfig.type === "warning" ? "⚠" : "?"}
                                </span>
                            </div>

                            <h3 className="text-base font-semibold text-gray-900 mb-2 text-center">
                                {confirmConfig.title}
                            </h3>

                            <p className="text-gray-600 text-sm mb-4 text-center">
                                {confirmConfig.message}
                            </p>

                            <div className={`flex gap-2 ${isMobile ? 'flex-col-reverse' : ''}`}>
                                <SmallButton
                                    variant="secondary"
                                    onClick={() => {
                                        setShowConfirmModal(false);
                                        if (confirmConfig.onCancel) confirmConfig.onCancel();
                                    }}
                                    className={isMobile ? 'w-full' : 'flex-1'}
                                >
                                    Cancel
                                </SmallButton>
                                <SmallButton
                                    variant={confirmConfig.type === "danger" ? "danger" : "primary"}
                                    onClick={() => {
                                        setShowConfirmModal(false);
                                        if (confirmConfig.onConfirm) confirmConfig.onConfirm();
                                    }}
                                    className={isMobile ? 'w-full' : 'flex-1'}
                                >
                                    Confirm
                                </SmallButton>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HighwayAddOwner;